/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import classnames from 'classnames';
import { reaction } from 'mobx';
import { observer } from 'mobx-react';
import dayjs, { Dayjs } from 'dayjs';
import React from 'react';
/* VSI customization START */
import Cookies from 'universal-cookie';
/* tslint:disable: no-duplicate-imports */
import { ArrayExtensions, getFallbackImageUrl } from '@msdyn365-commerce-modules/retail-actions';
import {
    addThrottledEvent, Alert, Button, getPayloadObject, getTelemetryAttributes, getTelemetryObject,
    INodeProps, IPayLoad, ITelemetryContent, Node, TelemetryConstant, Waiting
} from '@msdyn365-commerce-modules/utilities';
import {
    IAddToOrderTemplateDialogResources, IDuplicateItemsWhenAddingToOrderTemplateDialogResources,
    IItemAddedToOrderTemplateDialogResources, ILinesAddedToOrderTemplateDialogResources,
    IOrderTemplateNameDialogResources, IWishlistActionSuccessResult,
    SaveCartToOrderTemplateComponent
} from '@msdyn365-commerce/components';
import MsDyn365, {
    getUrlSync, IAny, ICoreContext, IGeneric, IImageSettings, RichTextComponent
} from '@msdyn365-commerce/core'; // 22192 MsDyn365
import { getCartState, ICartActionResult, ICartState } from '@msdyn365-commerce/global-state';
import {
    AsyncResult, AttributeValue, CartLine, CartLineValidationResults, LineDataValidationFailure
} from '@msdyn365-commerce/retail-proxy';
import {
    addCartLinesAsync, updateCartLinesAsync,
    validateForCheckoutAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import {
    getAttributeValuesAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import {
    getAddressFromZipCodeAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/StoreOperationsDataActions.g';
import {
    CategoryPathResult, LineDeliverySpecification, ReleasedProductType, SimpleProduct
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

// import { BonusCouponDetailsInput } from '../../actions/bonus-coupon-details.action';
/* VSI customization START */
import {
    IMFIBonusCouponsDetail, IMFIBonusItemDetails, IMFIBonusItemsResponse,
    IMFIGetBonusCouponsDetailsResponse, IMFIGetBonusItemsParameters
} from '../../actions/BonusItemDataServiceEntities.g';
import {
    GetCategoryPathsInput, getProductsCategoryPathsAction
} from '../../actions/get-mfrm-products-category-paths.action';
import { GetRecycleFeeCheckoutInput } from '../../actions/get-recycle-fee-checkout.action';
import { CartDataInfoInput, ICartProductInfo } from '../../data-actions/cart-state-sharing-action';
import {
    AttributesForSelectedVariantInput, getAttributesForCartLineItems, // , IProductWithAttribute
    IProductWithAttribute
} from '../../data-actions/get-product-attributes-cartlines';
import { PromoCodeComponent } from '../../themes/mattressfirm/views/components/promocode.component';
import { callUtagLink, callUtagView } from '../../Utilities/analytics/utagCaller';
import { getSetProductData, getSetProductSpec, getSetBonusItemProd, getSetSelectedBonasItem, getSetBonusCouponDetail, getSetPrevBonasModalState, getSetBonusItemBrandName, IMFRMDateGroupedProducts, getSetDateGroupedProducts } from '../../Utilities/cart-utils';
import { cartOperationDoRetry, updateCartLineDeliverySpecificationsInternal } from '../../Utilities/doRetryCallBack';
import { hasArray } from '../../Utilities/emtpyChecker';
import { getBlockZipCodeMessage } from '../../Utilities/get-block-message';
import { _getBonusCouponsDetails, _getMFIGetBonusItems } from '../../Utilities/get-bonus-items';
import _getInventoryQuantity from '../../Utilities/get-inventory-quantity';
import _getViewPort from '../../Utilities/get-view-port';
import { IOrderSummaryLines, OrderSummary } from '../../Utilities/order-summary-lineitem';
import { IBrandListData } from '../mfrm-buybox/mfrm-buybox.view';
/* VSI customization END */
import { CartLineGroupItems } from './components/mfrm-cart-line-groups';
import { CartLineItems, ICartlinesViewProps } from './components/mfrm-cart-line-items';
import {
    cartPromotions, ICartLineItemViewProps
} from './components/mfrm-cart-line-items-promotion';
import { IMfrmCartData } from './mfrm-cart.data';
import { IMfrmCartProps, IMfrmCartResources } from './mfrm-cart.props.autogenerated';
import { IDeliveryProps } from './mfrm-cart.view';
import {
    _coreProductATPCall, getSetBasketCookie, IBasketCookie, reshuffleItems, isOneOfChildBonusCoupons, setTealiumCartVariables
} from './utils/cart-utils';
import { getProductUrlSync } from '@msdyn365-commerce-modules/retail-actions';
import { getPromotionData, IPromotionData } from './utils/get-promotion-in-mfrm-cart';
import { PromoCodeAppliedOnCartInput } from '../../data-actions/promocode-applied-in-progress.action';
import { TriggerSpecialSynchronyFinancingInput } from '../../actions/trigger-special-synchrony-financing.action';
import { parseBrandName } from '../../Utilities/get-product-brand';
import { oneYearFromNow } from '../../Utilities/one-year-from-now';
import { MFIbonusitemsInput } from '../../data-actions/mfrm-mfi-bonusitem.action';
import { getGeoLocationZipCode } from '../../Utilities/get-zipcode';
export interface ICartViewProps extends IMfrmCartProps<IMfrmCartData> {
    className: string;
    title?: React.ReactNode;
    CartlinesWrapper: INodeProps;
    cartlines?: ICartlinesViewProps[];
    cartLinesGroup?: ICartlinesViewProps[][];
    cart: ICartState | undefined;
    orderSummaryHeading: React.ReactNode;
    orderSummaryLineitems?: IOrderSummaryLines;
    OrderSummaryWrapper: INodeProps;
    OrderSummaryItems: INodeProps;
    OrderSummaryErrors?: IOrderSummaryErrors;
    checkoutAsGuestButton: React.ReactNode;
    checkoutAsSignInUserButton: React.ReactNode;
    storeSelector?: React.ReactNode;
    backToShoppingButton: React.ReactNode;
    waitingComponent: React.ReactNode;
    promotionOptions?: ICartLineItemViewProps;
    promoCode: React.ReactNode;
    cartLoadingStatus: string;
    cartDataResult: boolean;
    mulitplePickUpEnabled?: boolean;
    createTemplateFromCartButton: React.ReactNode;
    telemetryContent?: ITelemetryContent;
    isTabletView: boolean;
    _renderUpdateZipCode: JSX.Element;
    _productDetails: IProductTealiums;
    disableCheckoutButtonState: boolean;
    showDeliveryStepper: boolean;
    // VSI Customization 52133
    showRemoveNotification?: boolean;
    showProductTitle?: string;
    isMobile: boolean;
    isDesktop: boolean;
    calculateTaxOnCart?: boolean;
    onOrderSummaryZipcodeClick(): void;
    handleTealiumClick(): void;
    removeItemClickHandler(cartlineToRemove: CartLine, url?: string): void;
    moveToWishlistSuccessHandler(result: IWishlistActionSuccessResult, cartlineId: CartLine): void;
}

export interface IOrderSummaryErrors {
    Wrapper: INodeProps;

    header: React.ReactNode;
    errors: React.ReactNode[];
}

export interface ICartWithStatus {
    cart: ICartState | undefined;
    status: string;
}

export interface IAttributeValueProduct extends AttributeValue {
    VariantRecordId?: number;
    Quantity?: number;
    ItemId?: string;
    leadTimeVal?: string;
    brandLeadTime?: string;
    outOfStock?: boolean;
    zipCodeError?: boolean;
    outOfStockSmallParcel?: boolean;
    availableQuantity?: number;
    categoryName?: string;
    revenueCategory?: string;
    quantityMessage?: boolean;

}

export interface ICoreDeliveryByMessage {
    itemID: string;
    variantID: string;
    deliveryByMessage: string;
    daySpan?: string;
    locationId?: string;
}
export interface ICartComponentState {
    mattressRecyclingFee: number | string;
    showPromotionDialog: boolean;
    cartlinesErrors: LineDataValidationFailure[];
    promotionData: IPromotionData;
    isTablet: boolean;
    cartRender?: boolean;
    showProductModal: boolean;
    addCtaGift: boolean;
    selectedBonusItems: IProductBonusItems[];
    ctaGiftButtonBoolean: boolean;
    isInput: boolean;
    isOrderSummaryZipCodeClicked: boolean;
    newZipCode: string;
    showZipCodeDelivery: string;
    showError: boolean;
    errorZipCode: string;
    showFindStore: boolean;
    disableCheckoutButton: boolean;
    productSpecifications: IProductDetails[];
    productCategoryPath: CategoryPathResult[];
    deliveryByMessagesCore: ICoreDeliveryByMessage[];
    deliveryByMessageSPDS: ICoreDeliveryByMessage[];
    productDetails: IProductDetails[];
    oomZipCode: boolean;
    showDeliveryStepper: boolean;
    previousBonasModal: IProductBonusItems[];
    currCartState?: ICartState;
    // VSI Customization 52133
    showRemoveNotification?: boolean;
    showProductTitle?: string;
    buttonDisabled?: boolean;
    isMobile?: boolean;
    isDesktop?: boolean;
    isBonusItemProductUpdated?: boolean;// Bug 71000 - Set true when BonusItemProduct value is being set
    // Store small parcel location in state
    smallParcelLocationDetails?: IProductLocationDetails[];
    productAttributes?: IProductWithAttribute[];
    pageCount: number;
}
// Small parcel locations
export interface IProductLocationDetails {
    itemId?: string;
    productId?: string;
    location?: string;
}

export interface IProductBonusItems {
    parentId: number | undefined;
    children: IMFIBonusItemDetails[];
}

export interface IProductBonusWithBrandName {
    productId: number | undefined;
    brandName: string;
}

export interface IRevenueDetails {
    Name: string;
    CategoryName: string;
}

export interface IProductTealiums {
    quantity?: string,
    subTotal?: string,
    productQuantity?: string[],
    productSize?: string[],
    productIDsArray?: string[],
    productNameArray?: string[],
    productPriceArray?: string[],
    cartProductPriceArray?: string[],
    variantRecord?: string[],
    productImageUrl?: string[],
    cartUrl?: string[]
    siteCookie?: string;
    productURL?: string;
    productCatgoryArray?: string;
    productBrandName?: string;
    productDiscountArray?: string;
    isCoreItem?: string;
    funnelType?: string;
}

export interface IProductDetails {
    typeOfProduct: string;
    VariantRecordId: number;
    outOfStock: boolean;
    Quantity: number;
    ItemId: string;
    leadTimeVal?: string;
    brandLeadTime?: string;
    ProducName?: string;
    MasterProductId?: number;
    zipCodeError?: boolean;
    outOfStockSmallParcel?: boolean;
    availableQuantity?: number;
    categoryName?: string;
    revenueCategory?: string;
    name: string;
    variantType: string;
    primaryImageUrl: string;
    fallbackImageUrl: string;
    quantityMessage: boolean;
    netPrice?: number;
    netAmountWithoutTax?: number;
    price?: number;
    discountedAmount?: number;
    brandName?: string;
    variantIDString?: string;
    brandAndProductName?: string;
    InventLocationId?: string;
    DeliveryDate?: string;
    deliveryTime?: string;
    RequestedDeliveryDate?: string;
    collection?: string;
}

/**
 *
 * Cart component
 * @extends {React.PureComponent<IMfrmCartProps<IMfrmCartData>>}
 */
@observer
class Cart extends React.Component<IMfrmCartProps<IMfrmCartData>, ICartComponentState> {
    /* VSI customization START */
    public globalBonusItems: IMFIBonusItemsResponse[] = [];
    public callFirstTime: boolean = false;
    public ctaGiftButton: boolean = false;
    public isGiftCoupon: IMFIBonusCouponsDetail[] = [];
    public getCoupons: IMFIGetBonusCouponsDetailsResponse[] = [];
    /* VSI customization END */
    private telemetryContent: ITelemetryContent;
    private payLoad: IPayLoad;
    private isQuantityLimitsFeatureEnabled: boolean = false;
    private readonly promotionPopupId: string = 'cart-promotion-popup';
    private resizeThrottledEventHandler?: (event: Event) => void;
    private coreProdLatestDelMsg: string = '';
    private timer: NodeJS.Timeout | null;

    constructor(props: IMfrmCartProps<IMfrmCartData>) {
        super(props);
        this.state = {
            cartlinesErrors: [],
            showPromotionDialog: false,
            promotionData: {},
            isTablet: false,
            cartRender: false,
            showProductModal: false,
            addCtaGift: false,
            selectedBonusItems: [],
            ctaGiftButtonBoolean: false,
            isInput: false,
            isOrderSummaryZipCodeClicked: false,
            newZipCode: '',
            showZipCodeDelivery: '',
            showError: false,
            errorZipCode: '',
            showFindStore: false,
            disableCheckoutButton: false,
            productSpecifications: [],
            productCategoryPath: [],
            deliveryByMessagesCore: [],
            deliveryByMessageSPDS: [],
            productDetails: [],
            oomZipCode: false,
            mattressRecyclingFee: this.props.resources.yetToBeCalculatedText,
            showDeliveryStepper: false,
            previousBonasModal: [],
            showRemoveNotification: false,
            showProductTitle: '',
            buttonDisabled: true,
            isBonusItemProductUpdated: false,
            productAttributes: this.props.data.productAttributes.result,
            pageCount: 0
        };
        this.telemetryContent = getTelemetryObject(this.props.context.request.telemetryPageName!, this.props.friendlyName, this.props.telemetry);
        this.payLoad = getPayloadObject('click', this.telemetryContent, '');
        this.promotionLinkClick = this.promotionLinkClick.bind(this);
        this.promotionCloseButtonclick = this.promotionCloseButtonclick.bind(this);
        this.timer = null;
    }

    public async componentWillMount() {
        // tslint:disable-next-line: no-floating-promises
        void this.tealiumCallOnViewCart(this.props.data.products).then();
    }
    // VSI Customization {52133} temp disabled max-func-body-length
    // tslint:disable-next-line:max-func-body-length
    public async componentDidMount(): Promise<void> {
        const {
            config: config,
            context: {
                actionContext: actionContext,
                request
            }
        } = this.props;
        const cookies = new Cookies();
        cookies.remove('cInfo_city');
        const zipcode = cookies.get('zip_code');
        const qtys = request.query?.qtys?.split(',');
        const pIds = request.query?.pids?.split(',');
        /* eslint-disable @typescript-eslint/no-floating-promises */
        !request.user.isAuthenticated && this.addLastAddedItemsIfCartEmpty();
        cookies.set('current_step', -1);
        cookies.set(`payment_review_dropship_latest_del_msg`, '', { path: '/' });
        cookies.set(`payment_review_parcel_latest_del_msg`, '', { path: '/' });
        cookies.set(`payment_review_latest_core_prod_msg`, '', { path: '/' });
        this.resetPromoStorageIfCartExpired();
        /* VSI customization START */
        const getLocalStorage = getSetSelectedBonasItem('get');
        // const getLocalStorage = localStorage.getItem('selectedBonasItems');
        /* VSI customization END*/
        let bonusItemArray: IProductBonusItems[] = [];
        if (getLocalStorage) {
            bonusItemArray = getLocalStorage;
        }
        getSetProductData('set', []);
        /* VSI customization START */
        getSetProductSpec('set', []);
        // localStorage.setItem('product_specification', '');
        /* VSI customization END*/
        const defaultZipCode = getGeoLocationZipCode(actionContext) || '';
        const directZipCode = zipcode || defaultZipCode;
        this.setState({ newZipCode: zipcode || defaultZipCode, showZipCodeDelivery: zipcode || defaultZipCode });
        if (qtys && pIds) {
            await this.addCartFromUrl();
        } else {
            const specifications = await this.getProductsSpecifications(this.props);
            this.setState({
                productSpecifications: specifications,
                selectedBonusItems: bonusItemArray
            });
            this._getStateFromZipCode(this.props, directZipCode, true);
        }
        this.setState({
            selectedBonusItems: bonusItemArray
        });

        this.isQuantityLimitsFeatureEnabled = await this._getOrderQuantityLimitsFeatureFlag();
        if (this.isQuantityLimitsFeatureEnabled) {
            await this._validateCart();
        }

        if (config.showAvailablePromotions) {
            if (this.props.data.cart.result?.cart.CartLines) {
                this.setState({ promotionData: await getPromotionData(actionContext) });
            }

            reaction(
                () => this.props.data.cart.result?.cart.CartLines,
                async () => {
                    this.setState({ promotionData: await getPromotionData(actionContext) });
                }
            );
        }
        /* VSI customization START */
        reaction(
            () => this.props.data.cart.result?.cart.Coupons,
            async () => {
                /* VSI customization START */
                const getBonasItemProduct: string | null = getSetBonusItemProd('get');
                // const getBonasItemProduct: string | null = localStorage.getItem('BonusItemProduct');
                /* VSI customization END*/
                if (getBonasItemProduct === null || getBonasItemProduct === undefined) {
                    this.props.data.cart.result?.cart.Coupons && this.props.data.cart.result.cart.Coupons?.length > 0 ? this._checkPromoCode(this.props) : '';
                    this.callFirstTime = true;
                }
            }
        );
        reaction(
            () => this.props.data.userDefinedPromoCode.result?.userDefinedPromos,
            async () => {
                if (this.props.data.userDefinedPromoCode.result && this.props.data.userDefinedPromoCode.result?.userDefinedPromos && this.props.data.userDefinedPromoCode.result?.userDefinedPromos.length > 0) {
                    await this._validateCart();
                    this._checkPromoCode(this.props);
                    const addedProds = getSetProductData('get');
                    // VSI Customization {52133} temp disabled no-floating-promises
                    // tslint:disable-next-line: no-floating-promises
                    this.checkSmallParcelOutOfStock(addedProds, this.props.context);
                }
            }
        );

        reaction(
            () => this.props.data.productAttributes.result,
            async () => {
                const specificationsData = await this.getProductsSpecifications(this.props);
                this.setState({
                    productSpecifications: specificationsData
                });
                const categoryPath = await this.getProductsCategoryPath(this.props, specificationsData);
                this.setState({
                    productCategoryPath: categoryPath
                });
                const cookies = new Cookies();
                const zip = cookies.get(`zip_code`);
                this._getStateFromZipCode(this.props, zip || directZipCode, true);
            }
        );
        if (MsDyn365.isBrowser) {
            this.resizeThrottledEventHandler = addThrottledEvent(window, 'resize', () => this.updateViewPort(this.props.context));
        }
        this.updateViewPort(this.props.context);
        this._addGiftCta();
        // tslint:disable-next-line: no-floating-promises
        this.updateCartDataSharing();
        // Resetting phone number
        cookies.set('customer_phone', '');
        cookies.set('selected_delivery_service', -2);
        // const cartState =  await getCartState(this.props.context.actionContext);
        // this.setState({currCartState: cartState, showRemoveNotification: false});
        this.setState({ showRemoveNotification: false, showProductTitle: '' });
        const showProductData = getSetProductData('get');
        showProductData.map((item: IProductDetails) => {
            if (item.brandName === 'Beautyrest' && (item.collection === 'Beyond' || item.collection === 'Beyond Hybrid')) {
                this.props.context.actionContext.update(new TriggerSpecialSynchronyFinancingInput(), { triggerSpecialFinancing: true });
            }
        });
        this._getAllCoupons(this.props);
        if (MsDyn365.isBrowser) {
            const cookie = new Cookies();
            cookie.set(`checkoutCurrentStep`, -1);
        }
        /* VSI customization END */
    }

    public async componentDidUpdate(): Promise<void> {
        const {
            data: {
                cart: { result }
            },
            context: {
                app: { config }
            }
        } = this.props;
        let isCoreItem: string;
        let funnelType: string;
        const isCheckCore = this.coreProductAvailable();
        if (isCheckCore) {
            isCoreItem = "true";
            funnelType = "full funnel";
        } else {
            isCoreItem = "false";
            funnelType = "no core item";
        }
        const cartLinesData = result?.cart.CartLines;
        const isCartEmpty = cartLinesData && cartLinesData.length === 0;
        const quantity = `${result?.cart.TotalItems}`;
        const subTotal = `${result?.cart.SubtotalAmount}`;
        const productQuantity: string[] = [];
        const { deliveryServices } = config;
        const CartArrayRemove: CartLine[] = [];
        cartLinesData?.map((item) => {
            const checkExists = !!deliveryServices?.find((val: { serviceProductRecId: number }) => val.serviceProductRecId === item.ProductId);
            if (checkExists) {
                CartArrayRemove.push(item);
            } else {
                productQuantity.push(`${item.Quantity}`);
            }
        });
        if (isCartEmpty) {
            if ('utag_data' in window) {
                // @ts-ignore
                utag_data['is_cart_active'] = "false";
                [
                    'cart_product_id',
                    'cart_product_category',
                    'cart_product_name',
                    'cart_product_price',
                    'cart_product_quantity',
                    'cart_product_variant',
                    'cart_product_size',
                    'cart_product_image_url',
                    'cart_product_url',
                    'cart_total_quantity',
                    'cart_subtotal',
                    'cart_product_name_1',
                    'cart_product_name_2',
                    'cart_product_name_3',
                    'cart_product_image_url_1',
                    'cart_product_image_url_2',
                    'cart_product_image_url_3',
                    'cart_url',
                    'core_item_flag',
                    'funnel_type'
                    // @ts-ignore
                ].forEach(e => delete utag_data[e]);
            }
        } else {
            const productDetailsUpdated = this.generateProductUpdateStrings();
            if ('utag_data' in window) {
                // @ts-ignore
                utag_data['cart_subtotal'] = `${productDetailsUpdated?.subTotal}`;
                // @ts-ignore
                utag_data['cart_total_quantity'] = `${productDetailsUpdated?.quantity}`;
                // @ts-ignore
                utag_data['cart_product_price'] = productDetailsUpdated?.cartProductPriceArray;
                // @ts-ignore
                utag_data['cart_product_id'] = productDetailsUpdated?.productIDsArray;
                // @ts-ignore
                utag_data['cart_product_image_url'] = productDetailsUpdated?.productImageUrl;
                // @ts-ignore
                utag_data['cart_product_quantity'] = productDetailsUpdated?.productQuantity;
                // @ts-ignore
                utag_data['cart_product_variant'] = productDetailsUpdated?.variantRecord;
                // @ts-ignore
                utag_data['cart_product_name'] = productDetailsUpdated?.productNameArray;
                // @ts-ignore
                utag_data['cart_url'] = productDetailsUpdated?.cartUrl;
                // @ts-ignore
                utag_data['cart_product_url'] = productDetailsUpdated?.productURL;
                // @ts-ignore
                utag_data['cart_product_size'] = productDetailsUpdated?.productSize;
                // @ts-ignore
                utag_data['cart_product_category'] = productDetailsUpdated?.productCatgoryArray;
                // @ts-ignore
                utag_data['cart_product_name_1'] = productDetailsUpdated?.productNameArray[0];
                // @ts-ignore
                utag_data['cart_product_name_2'] = productDetailsUpdated?.productNameArray[1];
                // @ts-ignore
                utag_data['cart_product_name_3'] = productDetailsUpdated?.productNameArray[2];
                // @ts-ignore
                utag_data['cart_product_image_url_1'] = productDetailsUpdated?.productImageUrl[0];
                // @ts-ignore
                utag_data['cart_product_image_url_2'] = productDetailsUpdated?.productImageUrl[1];
                // @ts-ignore
                utag_data['cart_product_image_url_3'] = productDetailsUpdated?.productImageUrl[2];
                // @ts-ignore
                utag_data['product_price'] = productDetailsUpdated?.productPriceArray;
                // @ts-ignore
                utag_data['product_id'] = productDetailsUpdated?.productIDsArray;
                // @ts-ignore
                utag_data['product_image_url'] = productDetailsUpdated?.productImageUrl;
                // @ts-ignore
                utag_data['product_quantity'] = productDetailsUpdated?.productQuantity;
                // @ts-ignore
                utag_data['product_variant'] = productDetailsUpdated?.variantRecord;
                // @ts-ignore
                utag_data['product_name'] = productDetailsUpdated?.productNameArray;
                // @ts-ignore
                utag_data['product_url'] = productDetailsUpdated?.productURL;
                // @ts-ignore
                utag_data['product_size'] = productDetailsUpdated?.productSize;
                // @ts-ignore
                utag_data['product_category'] = productDetailsUpdated?.productCatgoryArray;
                // @ts-ignore
                utag_data['product_brand'] = productDetailsUpdated?.productBrandName;
                // @ts-ignore
                utag_data['product_discount'] = productDetailsUpdated?.productDiscountArray;
                // @ts-ignore
                utag_data['core_item_flag'] = productDetailsUpdated?.isCoreItem;
                // @ts-ignore
                utag_data['funnel_type'] = productDetailsUpdated?.funnelType;
            }
        }
        const productDetails = { productQuantity, quantity, subTotal, isCoreItem, funnelType };
        setTealiumCartVariables('tealium_cart_products_quantity' , this.props.context, productDetails );
        if (CartArrayRemove.length > 0) {
            /* eslint-disable @typescript-eslint/no-floating-promises */
            // tslint:disable-next-line: no-floating-promises
            this._onlyRemoveItemFromCartHandler(CartArrayRemove[0]);
        }
        if (this.state.isOrderSummaryZipCodeClicked) {
            /* eslint-disable no-eval */
            // tslint:disable-next-line: no-eval
            eval(`var elem = document.querySelectorAll('.field-control--value'); if(elem != null && elem != undefined && elem.length > 0){ setTimeout(function(){elem[0].focus();}, 250);} else {console.log('element not found');}`);
            this.setState({ isOrderSummaryZipCodeClicked: false });
        }
        // tslint:disable: no-floating-promises
        this.checkAndUpdateBasketAndCartCookie();
        this.updateDateGroupedProductsData();
        if ((this.props.data.promoCodeApplicationState?.result?.isInProcess !== undefined && this.props.data.promoCodeApplicationState?.result?.isInProcess !== null) && (this.state.buttonDisabled !== this.props.data.promoCodeApplicationState?.result?.isInProcess)) {
            this.setState({ buttonDisabled: this.props.data.promoCodeApplicationState?.result?.isInProcess });
        }

    }

    /* VSI customization START create cart from URL*/
    public addCartFromUrl = async () => {
        const {
            data: {
                cart: { result }
            },
            context: {
                actionContext,
                request: {
                    apiSettings: { channelId, catalogId },
                    query
                }
            },
            telemetry
        } = this.props;
        const cookies = new Cookies();
        const zipcode = cookies.get('zip_code');
        const addProductToCart: CartLine[] = [];

        const qtys = query?.qtys?.split(',');
        const pIds = query?.pids?.split(',');
        const cartId = result?.cart?.Id!;
        const cartState = await getCartState(actionContext);

        if (qtys && pIds) {
            pIds.map((id, i) => {
                const checkExist = hasArray(cartState?.cart?.CartLines)?.find(item => item.ProductId === Number(id));
                if (!checkExist) {
                    addProductToCart.push({
                        Quantity: Number(qtys[i]),
                        ProductId: Number(id)
                    });
                }
            });

            if (!hasArray(result?.cart?.CartLines)) {
                await this._validateCart().then(async () => {
                    await addCartLinesAsync({ callerContext: actionContext }, cartId, addProductToCart).then(async newCart => {
                        await cartState.updateCart({ newCartObject: newCart });
                        getAttributesForCartLineItems(new AttributesForSelectedVariantInput(channelId, catalogId), actionContext).then((res) => {
                            actionContext.update(new AttributesForSelectedVariantInput(channelId, catalogId), res);
                            this.returnCoreProductCheck();
                            this._checkPromoCode(this.props);
                        }).catch((error) => {
                            telemetry.warning(error);
                            telemetry.debug('Unable to getAttributesForCartLineItems on product added Cart URLS');
                        });
                    });
                }).catch((error) => {
                    telemetry.warning(error);
                    telemetry.debug('Unable to validate Cart on product added Cart URLS');
                });
            } else {
                if (addProductToCart.length > 0) {
                    const cart = cartState.cart;
                    // clear the delivery information on retail server, so GetDeliveryOptions charge calculation works correctly
                    await cartState.clearCartLinesDeliveryInformation({ cart });
                    await this._validateCart().then(async () => {
                        await addCartLinesAsync({ callerContext: actionContext }, cartId, addProductToCart).then(async newCart => {
                            await cartState.updateCart({ newCartObject: newCart });
                            getAttributesForCartLineItems(new AttributesForSelectedVariantInput(channelId, catalogId), actionContext).then((res) => {
                                actionContext.update(new AttributesForSelectedVariantInput(channelId, catalogId), res);
                                this.returnCoreProductCheck();
                                this._checkPromoCode(this.props);
                            }).catch((error) => {
                                telemetry.warning(error);
                                telemetry.debug('Unable to getAttributesForCartLineItems on product already added Cart URLS');
                            });
                        });
                    }).catch((error) => {
                        telemetry.warning(error);
                        telemetry.debug('Unable to validate Cart on product already added Cart URLS');
                    });
                } else {
                    const specifications = await this.getProductsSpecifications(this.props);
                    this.setState({
                        productSpecifications: specifications
                    });
                    const categoryPath = await this.getProductsCategoryPath(this.props, specifications);
                    this.setState({
                        productCategoryPath: categoryPath
                    });
                    this._getStateFromZipCode(this.props, zipcode, false);
                }
            }
        }
    };
    /* VSI customization END - create cart from URL */

    public resetPromoStorageIfCartExpired = () => {
        const { telemetry } = this.props;
        const cookies = new Cookies();
        const lastCartExpiryTime = cookies.get('last_cart_expiry_time');
        const currDate = new Date();
        const lastCartExpiryDate = new Date(lastCartExpiryTime);
        try {
            if (currDate > lastCartExpiryDate) {
                localStorage.removeItem('promo_code_apply_list');
                localStorage.removeItem('promoCode');
                localStorage.removeItem('selectedBonasItems');
            }
        } catch (error) {
            telemetry.warning(error);
            telemetry.debug('Unable to remove items');
        }
    };

    public coreProductAvailable = (): boolean => {
        const {
            data: {
                productAttributes
            }
        } = this.props;
        const productAttributesData = productAttributes.result;
        let isCheckItemCore = false;
        for (let i = 0; i < productAttributesData?.length!; i++) {
            const newProductSpecificationData = productAttributesData![i].attributeValue;
            newProductSpecificationData.map((item) => {
                if (item.Name === 'shippingInformation') {
                    if (item?.TextValue?.trim().toLocaleLowerCase() === 'delivery') {
                        isCheckItemCore = true;
                    }
                }
            });
        }
        return isCheckItemCore;
    };

    public addProductToDropShipDateGroup = (deliveryDate: string, productVariantRecordId: number, daySpan?: string) => {
        const dateGroupedData: IMFRMDateGroupedProducts[] = getSetDateGroupedProducts('get');
        if (dateGroupedData && hasArray(dateGroupedData)) {
            for (const productData of dateGroupedData) {
                if (productData.productsType?.toLowerCase() === 'drop ship') {
                    if (productData.dateStr === deliveryDate) {
                        if (productData.productVariantRecordIds && (!productData.productVariantRecordIds.includes(productVariantRecordId))) {
                            productData.productVariantRecordIds.push(productVariantRecordId);
                            getSetDateGroupedProducts('set', dateGroupedData);
                            return;
                        } else if (productData.productVariantRecordIds && (productData.productVariantRecordIds.includes(productVariantRecordId))) {
                            return;
                        }
                    }
                }
            }
            const newDateEntry: IMFRMDateGroupedProducts = { dateStr: deliveryDate, productVariantRecordIds: [productVariantRecordId], productsType: 'drop ship', productItemIds: [], days: daySpan };
            dateGroupedData.push(newDateEntry);
            getSetDateGroupedProducts('set', dateGroupedData);
            return;
        } else {
            const newDateGroupedProducts: IMFRMDateGroupedProducts[] = [];
            const newDateEntry: IMFRMDateGroupedProducts = { dateStr: deliveryDate, productVariantRecordIds: [productVariantRecordId], productsType: 'drop ship', productItemIds: [], days: daySpan };
            newDateGroupedProducts.push(newDateEntry);
            getSetDateGroupedProducts('set', newDateGroupedProducts);
        }
    };

    public addProductToSmallParcelDateGroup = (deliveryDate: string, productVariantRecordId: number, daySpan?: string) => {
        const dateGroupedData: IMFRMDateGroupedProducts[] = getSetDateGroupedProducts('get');
        if (dateGroupedData && hasArray(dateGroupedData)) {
            for (const productData of dateGroupedData) {
                if (productData.productsType?.toLowerCase() === 'parcel') {
                    if (productData.dateStr === deliveryDate) {
                        if (productData.productVariantRecordIds && (!productData.productVariantRecordIds.includes(productVariantRecordId))) {
                            productData.productVariantRecordIds.push(productVariantRecordId);
                            getSetDateGroupedProducts('set', dateGroupedData);
                            return;
                        } else if (productData.productVariantRecordIds && (productData.productVariantRecordIds.includes(productVariantRecordId))) {
                            return;
                        }
                    }
                }
            }
            const newDateEntry: IMFRMDateGroupedProducts = { dateStr: deliveryDate, productVariantRecordIds: [productVariantRecordId], productsType: 'parcel', productItemIds: [], days: daySpan };
            dateGroupedData.push(newDateEntry);
            getSetDateGroupedProducts('set', dateGroupedData);
            return;
        } else {
            const newDateGroupedProducts: IMFRMDateGroupedProducts[] = [];
            const newDateEntry: IMFRMDateGroupedProducts = { dateStr: deliveryDate, productVariantRecordIds: [productVariantRecordId], productsType: 'parcel', productItemIds: [], days: daySpan };
            newDateGroupedProducts.push(newDateEntry);
            getSetDateGroupedProducts('set', newDateGroupedProducts);
        }
    };

    public updateDateGroupedProductsData = () => {
        const productsData: IProductDetails[] = getSetProductData('get');
        getSetDateGroupedProducts('set', []);
        if (productsData && hasArray(productsData)) {
            for (const productData of productsData) {
                if (productData.typeOfProduct?.toLowerCase() === 'parcel') {
                    if (hasArray(this.state.deliveryByMessageSPDS)) {
                        for (const deliveryByMessage of this.state.deliveryByMessageSPDS) {
                            if (deliveryByMessage.variantID === productData.VariantRecordId?.toString()) {
                                this.addProductToSmallParcelDateGroup(deliveryByMessage.deliveryByMessage, productData.VariantRecordId, deliveryByMessage.daySpan);
                            }
                        }
                    }
                } else if (productData.typeOfProduct?.toLowerCase() === 'drop ship') {
                    if (hasArray(this.state.deliveryByMessageSPDS)) {
                        for (const deliveryByMessage of this.state.deliveryByMessageSPDS) {
                            if (deliveryByMessage.variantID === productData.VariantRecordId?.toString()) {
                                this.addProductToDropShipDateGroup(deliveryByMessage.deliveryByMessage, productData.VariantRecordId, deliveryByMessage.daySpan);
                            }
                        }
                    }
                }
            }
        }
    };

    public checkAndUpdateBasketAndCartCookie = async () => {
        const {
            data: {
                cart: { result }
            },
            context
        } = this.props;
        const basketCookie: IBasketCookie = getSetBasketCookie('get', this.props.context);
        // Only store cartlines for guest user in basket cookie
        if (!basketCookie && !context.request.user.isAuthenticated) {
            const bCookie: IBasketCookie = {};
            bCookie.cartId = result?.cart.Id;
            getSetBasketCookie('set', context, bCookie, result?.cart?.CartLines);
        } else {
            if (result?.cart?.CartLines) {
                const baskCartInfo = basketCookie.cartDataInfo;
                if (baskCartInfo) {
                    if (result?.cart.CartLines?.length !== baskCartInfo.length) {
                        this.refreshCartCookie();
                    } else {
                        let requiresUpdate = false;
                        for (const c of result?.cart?.CartLines) {
                            let cartItemFound = false;
                            for (const bci of baskCartInfo) {
                                if (bci.productId === c.ProductId && bci.itemId === c.ItemId && bci.quantity === c.Quantity) {
                                    cartItemFound = true;
                                }
                            }
                            if (!cartItemFound) {
                                requiresUpdate = true;
                                break;
                            }
                        }
                        if (requiresUpdate) {
                            this.refreshCartCookie();
                        }
                    }
                } else {
                    this.refreshCartCookie();
                }
            }
        }
    };

    public addLastAddedItemsIfCartEmpty = async () => {
        const {
            data: {
                cart: { result }
            },
            context: { actionContext, request: { apiSettings: { channelId, catalogId } }
            }, telemetry
        } = this.props;
        if (hasArray(result?.cart?.CartLines)) {
            return;
        }
        if (result?.cart?.Id) {
            const basketCookie: IBasketCookie = getSetBasketCookie('get', this.props.context);
            if (basketCookie) {
                const basketCLs = basketCookie.cartDataInfo;
                if (basketCLs && basketCLs.length) {
                    const lastAddedCartlines: CartLine[] = [];
                    for (const bcl of basketCLs) {
                        lastAddedCartlines.push({ ProductId: bcl.productId, ItemId: bcl.itemId, Quantity: bcl.quantity, EntryMethodTypeValue: 5 });
                    }
                    const cartState = await getCartState(actionContext);
                    await addCartLinesAsync({ callerContext: actionContext }, result?.cart.Id, lastAddedCartlines).then(async newCart => {
                        await cartState.refreshCart({});

                        // Call attributes API to get attributes after adding cartlines
                        getAttributesForCartLineItems(new AttributesForSelectedVariantInput(channelId, catalogId), actionContext).then((res) => {
                            actionContext.update(new AttributesForSelectedVariantInput(channelId, catalogId), res);
                            this.returnCoreProductCheck();
                            this._checkPromoCode(this.props);
                            this.setState({ productAttributes: res });
                        })
                            .catch((error) => {
                                telemetry.warning(error);
                                telemetry.debug('Unable to getAttributesForCartLineItems when click to checkout');
                            });
                        this.refreshCartCookie();

                        const cookieForRefresh = new Cookies();
                        const zipCodeForRefresh = cookieForRefresh.get(`zip_code`);
                        this._getStateFromZipCode(this.props, zipCodeForRefresh, true);
                    });
                }
            }
        }
    };

    public prepareProductDataForTealiumCall = async (products: AsyncResult<SimpleProduct[]>, deletedItemId?: string | undefined) => {
        const {
            data: {
                cart: {
                    result: cart
                },
                productAttributes: { result: productAttributes }
            }
        } = this.props;
        const cartLinesData: CartLine[] | undefined = cart?.cart.CartLines;
        const originUrl = this.props.context.request.url.requestUrl.origin;
        const productIdArray: (string | undefined)[] = [];
        const productImageUrl: string[] = [];
        const productNameArray: (string | undefined)[] = [];
        const productCategoryArray: (string | undefined)[] = [];
        const productBrandArray: (string | undefined)[] = [];
        const productVariantIdArray: (string | undefined)[] = [];
        const productPriceArray: (string | undefined)[] = [];
        const productQuantityArray: (string | undefined)[] = [];
        const productSize: string[] = [];
        const productURL: string[] = [];
        const prodSpec = getSetProductData('get');
        /* VSI customization START - 79266 Tracking */
        let isCoreItem: string;
        let funnelType: string;
        const isCheckCore = this.coreProductAvailable();
        if (isCheckCore) {
            isCoreItem = "true";
            funnelType = "full funnel";
        } else {
            isCoreItem = "false";
            funnelType = "no core item";
        }
        /* VSI customization END - 79266 Tracking */
        hasArray(products?.result)?.map(item => {
            productIdArray.push(item?.ItemId);
            productNameArray.push(item?.Name);
            productImageUrl.push(`${item?.PrimaryImageUrl}`);
            const urlProduct = this.generateURLForPDP(item, this.props.context);
            productURL.push(`${originUrl}${urlProduct}`);
            const attr = hasArray(productAttributes)?.find(p => p?.productId === item?.RecordId)?.attributeValue;
            const brandName = parseBrandName(attr, this.props.context.app.config.brandBackofficeAttributePdp, item?.RecordId);
            productBrandArray.push(`${brandName}`);
            productCategoryArray.push(`${hasArray(productAttributes)?.find(p => p?.productId === item?.RecordId)?.categoryName}`);
        });
        hasArray(cartLinesData)?.map((items) => {
            let productDiscount: number = 0;
            items.DiscountLines?.map((sales: { DiscountCode: string; EffectiveAmount: number; }) => {
                if (sales.DiscountCode) {
                    productDiscount = productDiscount + sales.EffectiveAmount;
                }
            });
            const updatedPrice = Number(items.NetAmountWithoutTax!) + Number(productDiscount);
            productPriceArray.push(`${updatedPrice}`);
            productVariantIdArray.push(items?.Barcode);
            productQuantityArray.push(items?.Quantity?.toString());
            productSize.push(`${this.getVariant(this.getProduct(this.props.data.products.result!, items)).toString().replace(/,/g, '')}`);
        });
        /* VSI customization START - Tracking 79374 */
        if (deletedItemId) {
            const allProductsId = [...productIdArray];
            const indexOfDeletedItem = allProductsId?.indexOf(deletedItemId);
            allProductsId?.splice(indexOfDeletedItem, 1);
            const checkForCore = allProductsId?.find((item, i) => { return item === prodSpec[i]?.ItemId && prodSpec[i]?.typeOfProduct?.toLowerCase() === 'core'; });
            if (checkForCore) {
                isCoreItem = "true";
                funnelType = "full funnel";
            }
            else {
                isCoreItem = "false";
                funnelType = "no core item";
            }
        }
        /* VSI customization END - Tracking 79374 */
        return {
            productIdArray,
            productNameArray,
            productCategoryArray,
            productBrandArray,
            productVariantIdArray,
            productPriceArray,
            productQuantityArray,
            productImageUrl,
            productURL,
            productSize,
            isCoreItem,
            funnelType
        };
    };
    public tealiumCallOnViewCart = async (products: AsyncResult<SimpleProduct[]>) => {
        const getProduct = await this.prepareProductDataForTealiumCall(products);

        await callUtagLink(
            {
                event: 'checkout-steps',
                event_category: 'ecommerce',
                event_action: 'checkout steps',
                event_label: 'Step 1 : View Order',
                event_noninteraction: 'false',
                enhanced_action: 'checkout',
                event_type: 'view cart',
                checkout_step: '1',
                product_id: getProduct?.productIdArray,
                product_name: getProduct?.productNameArray,
                product_category: getProduct?.productCategoryArray,
                product_brand: getProduct?.productBrandArray,
                product_variant: getProduct?.productVariantIdArray,
                product_price: getProduct?.productPriceArray,
                product_quantity: getProduct?.productQuantityArray,
                product_size: getProduct?.productSize,
                product_url: getProduct?.productURL,
                product_image_url: getProduct?.productImageUrl,
                core_item_flag: getProduct?.isCoreItem,
                funnel_type: getProduct?.funnelType
            }
        );
    };

    /* VSI customization START - Tracking 79374 */
    public tealiumCallOnRemoveForGA = async (products: AsyncResult<SimpleProduct[]>, deletedItemId?: string | undefined) => {
        const getProduct = await this.prepareProductDataForTealiumCall(products, deletedItemId);
        const pageName = `https://www.mattressfirm.com/cart?remove_item_${this.state.pageCount}`;
        await callUtagView(
            {
                ga_title: "Cart",
                ga_location: pageName,
                core_item_flag: getProduct?.isCoreItem,
                funnel_type: getProduct?.funnelType
            }
        );
        this.setState({
            pageCount : this.state.pageCount + 1
        });
    };
    /* VSI customization END - Tracking 79374 */

    public onOrderSummaryZipcodeClick = () => {
        this.setState({ isInput: true, newZipCode: this.state.newZipCode, isOrderSummaryZipCodeClicked: true });
    };
    public handleTealiumClick = () => {
        const {
            context: { actionContext },
            app: {
                routes: {
                    backToShopping: { destinationUrl }
                }
            }
        } = this.props;
        const backToShoppingUrl = getUrlSync('home', actionContext);
        const backToShoppingUrlNavigate = destinationUrl || getUrlSync('home', actionContext);
        const getPath = MsDyn365.isBrowser && window.location.pathname;

        void callUtagLink(
            {
                event: 'internal-navigation',
                event_category: 'internal links',
                event_action: `to ${backToShoppingUrl}`,
                event_label: `from continue shopping btn: ${getPath}`,
                event_noninteraction: 'false'
            }
        );

        window.location.href = backToShoppingUrlNavigate;
    };
    public componentWillUnmount(): void {
        MsDyn365.isBrowser && window.removeEventListener('resize', this.resizeThrottledEventHandler!, false);
        if (this.timer) {
            clearTimeout(this.timer);
        }
    }

    public refreshCartCookie = () => {
        const {
            context,
            data: {
                cart: { result }
            }
        } = this.props;
        const basketCookie: IBasketCookie = getSetBasketCookie('get', context);
        // Basket cookie only stores guest user's cartlines
        if (basketCookie && !context.request.user.isAuthenticated) {
            basketCookie.cartId = result?.cart.Id;
            getSetBasketCookie('set', context, basketCookie, result?.cart?.CartLines);
        }
    };

    public handleClickCheckout = async () => {
        const {
            context: {
                actionContext
            },
            data: {
                cart: { result: cart }
            },
            telemetry
        } = this.props;
        if (this.state.showError) {
            return;
        }
        const cartLinesData: CartLine[] | undefined = cart?.cart.CartLines;
        const sepcProducts = this.state.productSpecifications;
        const removeOutStockCartLines: string[] = [];
        const guestCheckoutUrl = getUrlSync('checkout', actionContext) || '';
        const itemsIDs: string[] = [];
        const result: CartLine[] | undefined = cartLinesData?.filter(itemCart => sepcProducts.some(itemProduct => itemCart.ProductId === itemProduct.VariantRecordId && (itemProduct.outOfStock === true || itemProduct.zipCodeError === true)));
        result?.forEach((item) => {
            removeOutStockCartLines.push(item.LineId?.toString()!);
            itemsIDs.push(item?.ItemId?.toString()!);
        });
        if (removeOutStockCartLines.length > 0) {
            const input = {
                cartLineIds: removeOutStockCartLines
            };
            await cart?.removeCartLines(input).then(() => {
                this._validateCart()
                    .catch((error) => {
                        telemetry.warning(error);
                        telemetry.debug('Unable to validate Cart when remove cart lines');
                    });
                this.setState({
                    cartRender: false
                });
                this._getUpdateLinbeDeliveryCall(this.props, this.state.newZipCode, this.state.errorZipCode, '');
                this.refreshCartCookie();
                itemsIDs.map((items) => {
                    const productSpecificationsCookieData = getSetProductData('get');
                    const producDetailsCookieData = getSetProductData('get');
                    const checkAvailable = productSpecificationsCookieData && productSpecificationsCookieData.filter((singleItem: IProductDetails) => singleItem.ItemId !== items);
                    const checkAvailableData = producDetailsCookieData && producDetailsCookieData.filter((singleItem: IProductDetails) => singleItem.ItemId !== items);
                    getSetProductData('set', checkAvailableData);
                    getSetProductSpec('set', checkAvailable);
                });
                const cartLinesUpdateData: CartLine[] | undefined = cart?.cart.CartLines;
                if (cartLinesUpdateData?.length! > 0) {
                    window.location.href = guestCheckoutUrl;
                }
            })
                .catch((error) => {
                    telemetry.warning(error);
                    telemetry.debug('Unable to Remove Cart Line when click to checkout');
                });
        } else {
            window.location.href = guestCheckoutUrl;
        }
    };

    public returnCoreProductCheck = () => {
        const { data } = this.props;
        const { productAttributes } = this.state;
        let showDeliveryStep: boolean = false;
        // if(!MsDyn365.isBrowser) {
        // If attribute array is empty after updating something, use state attributes
        let productAttributesList: IProductWithAttribute[] | undefined;
        if (productAttributesList && productAttributesList.length > 0) {
            productAttributesList = data?.productAttributes.result;
        } else {
            productAttributesList = productAttributes;
        }
        if (productAttributesList && productAttributesList.length > 0) {
            for (const productSpecItem of productAttributesList) {
                productSpecItem.attributeValue.find((shippingData) => {
                    if (shippingData?.TextValue && shippingData.TextValue.trim().toLocaleLowerCase() === 'delivery') {
                        showDeliveryStep = true;
                    }
                });
            }
        }
        return showDeliveryStep;
    };

    public getFinalPrice = () => {
        const {
            data: {
                cart: { result: cart }
            }
        } = this.props;
        const TotalSalesAmount = cart?.cart.TotalSalesAmount || 0;
        const finalPrice = this.state.isTablet ? ` - ${TotalSalesAmount.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD'
        })
            }` : '';
        return finalPrice ? finalPrice : '';
    };

    // tslint:disable-next-line:max-func-body-length cyclomatic-complexity
    public render(): JSX.Element | null {
        const {
            data: {
                cart: { result: cart },
                products: { result: products }
            },
            context: {
                request: {
                    user: {
                        isAuthenticated
                        /* VSI customization START - 22192 */
                        // signInUrl
                        /* VSI customization END - 22192 */
                    }
                },
                actionContext

            },
            resources
        } = this.props;
        const { buttonDisabled } = this.state;
        !this.callFirstTime && this._checkPromoCode(this.props);
        let cartLines = this._getCartLines();
        const totalPrice = this.getFinalPrice();
        const zipCartLines: ICartlinesViewProps[] | null = [];
        const outOfStockCartLines: ICartlinesViewProps[] | null = [];
        let sortCartLine: ICartlinesViewProps[] | null = [];
        if (this.state.productSpecifications.length > 0) {
            cartLines?.map((data) => {
                const productID = data.data?.product?.RecordId;
                const findProduct = this.state.productSpecifications.find(item => item?.VariantRecordId === productID);
                if (findProduct) {
                    if (productID === findProduct.VariantRecordId) {
                        if (findProduct.zipCodeError) {
                            zipCartLines.push(data);
                        } else if (findProduct.outOfStock) {
                            outOfStockCartLines.unshift(data);
                        } else {
                            const checkProductStockCart = outOfStockCartLines.includes(data);
                            if (!checkProductStockCart) {
                                outOfStockCartLines.push(data);
                            }
                        }
                    }
                } else {
                    const checkProductStockCart = outOfStockCartLines.includes(data);
                    if (!checkProductStockCart) {
                        outOfStockCartLines.push(data);
                    }
                }
            });
        }
        sortCartLine = [...zipCartLines, ...outOfStockCartLines];
        if (sortCartLine.length > 0) {
            cartLines = sortCartLine;
        }
        const backToShoppingUrl = getUrlSync('home', actionContext) || '';
        /* VSI customization START - 22192 */
        // const checkoutURL = this._getCheckoutURL(isAuthenticated, guestCheckoutUrl, signInUrl || '');
        /* VSI customization END */
        const isCartEmpty = cart && cart.cart.CartLines && cart.cart.CartLines.length === 0;
        /* VSI customization START */
        // this._addGiftCta();
        const newCartLine: ICartlinesViewProps[] | null = [];
        const defualtCartLines: ICartlinesViewProps[] | null = [];
        // add bonus product beneath parent code if cartline item greater than 2
        if (cartLines && cartLines?.length > 2) {
            if (MsDyn365.isBrowser) {
                /* VSI customization START */
                const getLocalStorageData = getSetBonusItemProd('get');
                // const getLocalStorageData = localStorage.getItem('BonusItemProduct');
                /* VSI customization END*/
                let bonusItemArrayData: IMFIBonusItemsResponse[] = [];
                if (getLocalStorageData) {
                    bonusItemArrayData = getLocalStorageData;
                    hasArray(bonusItemArrayData)?.map((bonusItemArray: IMFIBonusItemsResponse) => {
                        bonusItemArray.BonusItemsDetails?.map((BonusItemsDetail) => {
                            BonusItemsDetail.Items?.map((item) => {
                                // filter out parent product from previous cartline
                                cartLines?.filter((cartlineData) => {
                                    if (item.QualifyingItemId === cartlineData.data?.cartline.ItemId) {
                                        newCartLine.push(cartlineData);
                                    }
                                    item.BonusItems?.map((bonusItemfinal) => {
                                        // filter out child product from previous cartline
                                        if (bonusItemfinal.BonusItemId === cartlineData.data?.cartline.ItemId) {
                                            newCartLine.push(cartlineData);// child
                                        }
                                    });
                                });
                            });
                        });
                    });
                }
            }

            const newCartLinesItemIds: string[] = [];
            newCartLine.map((item) => {
                if (item.data?.cartline.ItemId !== undefined) {
                    newCartLinesItemIds.push(item.data?.cartline.ItemId);
                }
            });

            cartLines?.filter((cartLineSingleData: ICartlinesViewProps) => {
                if (cartLineSingleData.data?.cartline.ItemId !== undefined && newCartLinesItemIds.indexOf(cartLineSingleData.data?.cartline.ItemId) === -1) {
                    defualtCartLines.push(cartLineSingleData);
                }
            });
        }
        // merge parent product and child product with previous cartline.
        for (let i = 0; i < defualtCartLines.length; i++) {
            newCartLine.unshift(defualtCartLines[i]);
            // cartLines = newCartLine;
        }
        /* VSI customization END */
        const RetailMulitplePickupFeatureState = this.props.data.featureState.result?.find(featureState => featureState.Name === 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature');
        const cartLinesGroup = RetailMulitplePickupFeatureState?.IsEnabled ? this._getCartLinesGroup() : null;

        this.payLoad.contentAction.etext = TelemetryConstant.Checkout;
        const checkoutBySignInAttributes = getTelemetryAttributes(this.telemetryContent, this.payLoad);
        this.payLoad.contentAction.etext = TelemetryConstant.GuestCheckout;
        const checkoutByGuestAttributes = getTelemetryAttributes(this.telemetryContent, this.payLoad);
        this.payLoad.contentAction.etext = TelemetryConstant.BackToShopping;
        const backtoShoppingAttributes = getTelemetryAttributes(this.telemetryContent, this.payLoad);
        const hasErrors = cartLines ? cartLines.some(cartline => cartline.error !== undefined) : false;
        reshuffleItems(cart?.cart?.CartLines ? cart?.cart?.CartLines : []);
        // tslint:disable-next-line:max-func-body-length
        const viewProps = {
            ...this.props,
            className: classnames('ms-cart'),
            title: <h1 className={'msc-cart__heading'} id='cart-delivery-message' tabIndex={0}><span tabIndex={-1}>{resources.cartTitle}</span></h1>,
            CartlinesWrapper: { className: 'msc-cartline-wraper' },
            OrderSummaryWrapper: { className: 'msc-order-summary-wrapper' },
            OrderSummaryItems: { className: 'msc-order-summary__items' },
            cartlines: cartLines,
            // VSI Cust 52133
            showRemoveNotification: this.state.showRemoveNotification,
            showProductTitle: this.state.showProductTitle,
            cartLinesGroup: cartLinesGroup,
            promoCode: this._getPromoCode(),
            promotionOptions: this._getPromotions(),
            cart: this.props.data.cart.result,
            context: this.props.context,
            typeName: this.props.typeName,
            telemetry: this.props.telemetry,
            _renderUpdateZipCode: this._renderUpdateZipCode(),
            _productDetails: this.generateProductData(),
            disableCheckoutButtonState: this.state.disableCheckoutButton,
            id: this.props.id,
            mulitplePickUpEnabled: RetailMulitplePickupFeatureState?.IsEnabled,
            removePromoAriaLabelFormat: resources.removePromoAriaLabelFormat,
            orderSummaryHeading: ArrayExtensions.hasElements(cart?.cart.CartLines) ?
                (
                    <h2 className={'msc-order-summary__heading'}>{resources.orderSummaryTitle}</h2>
                ) : null,
            orderSummaryLineitems: this._getOrderSummaryLineItems(),
            checkoutAsSignInUserButton: (!isCartEmpty) ?
                (
                    <Button onClick={this.handleClickCheckout} disabled={hasErrors} role='button' href='javascript:void(0)' className={`msc-cart__btn-checkout ${buttonDisabled && 'disabled'}`} title={resources.checkoutButtonTitle}{...checkoutBySignInAttributes} >
                        {resources.checkoutButtonTitle}{totalPrice}</Button>
                ) : null,
            checkoutAsGuestButton: (!isCartEmpty && !isAuthenticated) ?
                (
                    // tslint:disable-next-line: max-line-length
                    <Button onClick={this.handleClickCheckout} disabled={hasErrors} href='javascript:void(0)' className={`msc-cart__btn-checkout ${buttonDisabled && 'disabled'}`} title={resources.guestCheckoutButtonTitle}{...checkoutByGuestAttributes}>{resources.guestCheckoutButtonTitle}</Button>
                ) : null,
            backToShoppingButton: <Button className={'msc-cart__btn-backtoshopping'} {...backtoShoppingAttributes} href={backToShoppingUrl} title={resources.backToShoppingButtonTitle}>{resources.backToShoppingButtonTitle}</Button>,
            createTemplateFromCartButton: (this._displayAddCartToTemplate() && products) ?
                (
                    <SaveCartToOrderTemplateComponent
                        addToOrderTemplateDialogResources={this._generateAddToOrderTemplateDialogResources(resources)}
                        createOrderTemplateDialogResources={this._generateOrderTemplateNameDialogResources(resources)}
                        linesAddedToOrderTemplateDialogResources={this._generateLinesAddedToOrderTemplateDialogResources(resources)}
                        createOrderTemplateFromCartButton={resources.createOrderTemplateFromCartButton}
                        createOrderTemplateFromCartButtonTooltip={resources.createOrderTemplateFromCartButtonTooltip}
                        context={this.props.context}
                        id={this.props.id}
                        typeName={this.props.typeName}
                        data={{
                            orderTemplates: this.props.data.orderTemplates.result!,
                            cartLines: this.props.data.cart.result!.cart.CartLines!,
                            customerInformation: this.props.data.customerInformation.result
                        }}
                    />
                ) : null,
            storeSelector: this.props.slots.storeSelector && this.props.slots.storeSelector.length > 0 ? this.props.slots.storeSelector[0] : null,
            removeItemClickHandler: this._removeItemFromCartHandler,
            moveToWishlistSuccessHandler: this._moveToWishlistSuccessHandler,
            waitingComponent: <Waiting className='msc-waiting-circular msc-waiting-lg' />,
            cartLoadingStatus: this.props.data.cart.status === 'FAILED' ? this.props.resources.cartLoadingErrorMessage : null,
            cartDataResult: this.props.data.cart.result && this.props.data.cart.result.isEmpty,
            OrderSummaryErrors: this._getOrderSummaryErrors(cartLines),
            telemetryContent: this.telemetryContent,
            isTabletView: this.state.isTablet,
            calculateTaxOnCart: this.props.context.app.config.calculateTaxOnCart,
            onOrderSummaryZipcodeClick: this.onOrderSummaryZipcodeClick,
            handleTealiumClick: this.handleTealiumClick,
            showDeliveryStepper: this.returnCoreProductCheck(), // this.state.showDeliveryStepper
            isDesktop: this.state.isDesktop,
            isMobile: this.state.isMobile
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }
    ///// for payment review //////////
    private updateCartDataSharing = () => {
        // ontext.actionContext.update(new CheckoutZipCodeInfoInput(), { isValidZipCode: flag, updatedZipCode: zip });
        const cartProductsData: ICartProductInfo[] = [];
        const productSpecificationsCookieData = getSetProductData('get');
        if (productSpecificationsCookieData) {
            for (const productSpecItem of productSpecificationsCookieData) {
                const cLines = this.props.data.cart.result?.cart.CartLines;
                if (cLines) {
                    for (const cl of cLines) {
                        if (cl.ProductId === productSpecItem.VariantRecordId) {
                            cartProductsData.push({ productId: cl.ProductId ? cl.ProductId : -1, typeOfProduct: productSpecItem.typeOfProduct, deliveryDate: '' });
                        }
                    }
                }
            }
        }
        this.props.context.actionContext.update(new CartDataInfoInput(), { cartProductsInfo: cartProductsData });
    };

    // tslint:disable: no-floating-promises
    // tslint:disable-next-line: cyclomatic-complexity
    private async _updateATPMessagesForCoreProducts(): Promise<void> {
        const {
            data: {
                cart: { result }
            },
            context: {
                app: {
                    config: { defaultDeliveryMessage }
                }
            },
            telemetry
        } = this.props;
        const cookies = new Cookies();
        cookies.set(`payment_review_latest_core_prod_msg`, '', { path: '/' });
        this.coreProdLatestDelMsg = '';
        const productSpecificationsCookieData = getSetProductData('get');
        const allCoreProdMsgs: ICoreDeliveryByMessage[] = [];
        if (productSpecificationsCookieData) {
            for (const productSpecItem of productSpecificationsCookieData) {
                if (productSpecItem.typeOfProduct === 'Core') {
                    const cLines = result?.cart.CartLines;
                    if (cLines) {
                        for (const cl of cLines) {
                            if (cl.ProductId === productSpecItem.VariantRecordId) {
                                const respStr = await _coreProductATPCall(this.props, productSpecItem.VariantRecordId, productSpecItem.ItemId, cl.Quantity ? cl.Quantity : productSpecItem.Quantity);
                                const splittedData = respStr.split(',');
                                if (splittedData && splittedData.length && splittedData[2]) {
                                    if (splittedData[2] === defaultDeliveryMessage) {
                                        cookies.set(`payment_review_latest_core_prod_msg`, defaultDeliveryMessage, { path: '/' });
                                        this.coreProdLatestDelMsg = defaultDeliveryMessage;
                                    }
                                }
                                // MMM D
                                if (this.coreProdLatestDelMsg === '') {
                                    cookies.set(`payment_review_latest_core_prod_msg`, splittedData[3] === 'yes' ? '' : splittedData[2], { path: '/' });
                                    this.coreProdLatestDelMsg = splittedData[3] === 'yes' ? '' : splittedData[2];
                                } else if (splittedData[2] === defaultDeliveryMessage) {
                                    cookies.set(`payment_review_latest_core_prod_msg`, splittedData[3] === 'yes' ? '' : splittedData[2], { path: '/' });
                                    this.coreProdLatestDelMsg = splittedData[3] === 'yes' ? '' : splittedData[2];
                                } else if ((splittedData[2] !== defaultDeliveryMessage || !splittedData[2].includes(defaultDeliveryMessage)) && this.coreProdLatestDelMsg.includes('Delivery by ')
                                    && (this.coreProdLatestDelMsg !== defaultDeliveryMessage || !this.coreProdLatestDelMsg.includes(defaultDeliveryMessage))) {
                                    const thisMsgSplitted = splittedData[2].split('Delivery by ');
                                    const thisMsgDate = dayjs(thisMsgSplitted[1]).format('MMM D');
                                    const latestMsgSplitted = this.coreProdLatestDelMsg.split('Delivery by ');
                                    const latestMsgDate = dayjs(latestMsgSplitted[1]).format('MMM D');
                                    if (thisMsgDate > latestMsgDate) {
                                        cookies.set(`payment_review_latest_core_prod_msg`, splittedData[3] === 'yes' ? '' : splittedData[2], { path: '/' });
                                        this.coreProdLatestDelMsg = splittedData[3] === 'yes' ? '' : splittedData[2];
                                    }
                                } else {
                                    cookies.set(`payment_review_latest_core_prod_msg`, defaultDeliveryMessage, { path: '/' });
                                    this.coreProdLatestDelMsg = defaultDeliveryMessage;
                                }
                                allCoreProdMsgs.push({
                                    itemID: splittedData[0],
                                    variantID: splittedData[1],
                                    deliveryByMessage: splittedData[3] === 'yes' ? '' : splittedData[2],
                                    locationId: splittedData[4]
                                });
                                if (splittedData[3] === 'yes') {
                                    this.setState({ oomZipCode: true });
                                    const result = this.checkCoreProductErrorZipCode(this.state.productDetails);
                                    result.then((products) => {
                                        this.setState({
                                            productSpecifications: products,
                                            productDetails: products,
                                            showDeliveryStepper: true
                                        });
                                        getSetProductData('set', products);
                                    }).catch((error) => {
                                        telemetry.warning(error);
                                        telemetry.debug('Unable to Check core product zip code out of market');
                                    });
                                } else {
                                    const result = this.checkCoreProductErrorZipCode(this.state.productDetails);
                                    result.then((products) => {
                                        this.setState({
                                            productSpecifications: products,
                                            productDetails: products,
                                            showDeliveryStepper: true
                                        });
                                        getSetProductData('set', products);
                                    }).catch((error) => {
                                        telemetry.warning(error);
                                        telemetry.debug('Unable to Check core product zip code');
                                    });
                                }
                            }
                        }
                    }

                    if (allCoreProdMsgs.length > 0) {
                        this.setState({ deliveryByMessagesCore: allCoreProdMsgs, showDeliveryStepper: true });
                    }
                }
            }
        }
    }

    // Get value from ATP calls and Update warehouseId & fulfillmentStore ID against each cartline respectively
    private _updateCartLineWarehouseDetails = async () => {
        try {
            // Make a new array of small parcel and core products for location details
            const allProductLocations: IProductLocationDetails[] = [];
            // Core products's warehouse will only be set if calculateTaxOnCart is true
            this.props.context.app.config.calculateTaxOnCart && this.state.deliveryByMessagesCore?.forEach(prodMsg => {

                allProductLocations.push({
                    itemId: prodMsg.itemID,
                    productId: prodMsg.variantID,
                    location: prodMsg.locationId === 'undefined' || prodMsg.locationId === 'null' ? '' : prodMsg.locationId
                });
            });
            // Small parcel products
            this.state.smallParcelLocationDetails?.forEach(prodMsg => {
                allProductLocations.push({
                    itemId: prodMsg.itemId,
                    productId: prodMsg.productId,
                    location: prodMsg.location === 'undefined' || prodMsg.location === 'null' ? '' : prodMsg.location
                });
            });

            // Now as zip code is updated so get locationId from 'MFIATPInventoryStatus' API response above and updateCartLines()
            const cartState = await getCartState(this.props.context.actionContext);
            const currentCartLines = cartState.cart.CartLines;
            // Dropship Default Warehouse
            const defaultWarehouse = this.props.context.app.config.dropShipDefaultWarehouse || '099012';

            if (currentCartLines && currentCartLines.length > 0) {
                // Add locationId in CartLines to pass in payload
                const updatedCartLines = currentCartLines.map(cartLine => {
                    // Check if product is dropShip type
                    const checkProduct = this.state.productSpecifications.find((item) => item.VariantRecordId === cartLine.ProductId);
                    const typeProduct = checkProduct?.typeOfProduct;
                    if (typeProduct?.toLowerCase() === 'drop ship') {
                        // Set default warehouse
                        // cartLine.FulfillmentStoreId = defaultWarehouse;
                        cartLine.WarehouseId = defaultWarehouse;
                    } else {
                        const cartLineLocationDetails = allProductLocations.find(cartLineDeliveryData => cartLineDeliveryData.itemId === cartLine.ItemId && cartLineDeliveryData.productId === cartLine.ProductId?.toString())?.location;
                        const locationId = cartLineLocationDetails ? cartLineLocationDetails : '';
                        // cartLine.FulfillmentStoreId = locationId;
                        cartLine.WarehouseId = locationId;
                    }
                    return cartLine;
                });
                await updateCartLinesAsync({ callerContext: this.props.context.actionContext }, cartState.cart.Id, updatedCartLines).then(async result => {
                    const updatedCartState = await getCartState(this.props.context.actionContext);
                    await updatedCartState.refreshCart({});
                }).catch();
            }
        } catch (error) {
            this.props.telemetry.debug('Unable to update cartlines');
        }
    };

    private updateViewPort = (context: ICoreContext): void => {
        const viewport = _getViewPort(context);
        if (viewport === 'xs' || viewport === 'sm' || viewport === 'md') {
            this.setState({
                isTablet: true,
                isMobile: true,
                isDesktop: false
            });
        } else {
            this.setState({
                isTablet: false,
                isMobile: false,
                isDesktop: true
            });
        }
    };

    private promotionLinkClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        this.setState({
            showPromotionDialog: !this.state.showPromotionDialog
        });
    };

    // tslint:disable-next-line: function-name
    private async getProductsSpecifications(props: IMfrmCartProps<IMfrmCartData>): Promise<IProductDetails[]> {
        const {
            data: {
                products: { result: products },
                productAttributes
            },
            context: {
                app: {
                    config: {
                        brandBackofficeAttributePdp,
                        brandList,
                        smallParcelAttributeKey,
                        outOfStockProductKey,
                        deliveryMessageManagementValues,
                        leadTime
                    }
                },
                actionContext
            }
        } = props;
        const getProducts: SimpleProduct[] | undefined = products?.filter(Boolean);
        const productAttributesData = productAttributes.result;
        if (productAttributesData && productAttributesData.length > 0) {
            for (const productSpecItem of productAttributesData) {

                // if(productSpecItem. === 'Core') {
                //     includeDeliveryInStepper = true;
                // }
                productSpecItem.attributeValue.find((shippingData) => {
                    if (shippingData?.TextValue && shippingData.TextValue.trim().toLocaleLowerCase() === 'delivery') {
                        // includeDeliveryInStepper = true;
                        this.setState({ showDeliveryStepper: true });
                    }
                });
            }
        }
        const detailsProduct: IProductDetails[] = [];
        const localStorageProductDetails: IProductDetails[] = [];
        for (let i = 0; i < productAttributesData?.length!; i++) {
            const productRecordId = productAttributesData![i].productId;
            const newProductSpecificationData = productAttributesData![i].attributeValue;
            let typeOfProduct = '';
            let outOfStock: boolean = false;
            let outOfStockSmallParcel: boolean = false;
            let leadTimeVal: string | undefined = '';
            let brandLeadTime: string = '';
            let collection: string | undefined = '';
            collection = (newProductSpecificationData.find(item => item.Name?.trim().toLocaleLowerCase() === 'Collection'?.trim().toLocaleLowerCase())?.TextValue);
            const brand = brandBackofficeAttributePdp && newProductSpecificationData?.find(attr => attr.Name?.trim() === brandBackofficeAttributePdp)?.TextValue?.toLowerCase();
            if (!!brand) {
                brandList?.map((brandList: IBrandListData) => {
                    if (brandList.brandName.toLowerCase() === brand) {
                        brandLeadTime = (brandList?.brandLeadtime !== undefined && brandList?.brandLeadtime !== '' && brandList?.brandLeadtime !== null) ? brandList?.brandLeadtime : '';
                    }
                });
            }
            deliveryMessageManagementValues?.map((value: { shippingInformation: string }) => {
                const getShippingInfo = !!newProductSpecificationData.find(item => item.Name === smallParcelAttributeKey && item.TextValue && item.TextValue?.trim().toLowerCase() === value.shippingInformation?.trim().toLowerCase());
                leadTimeVal = (newProductSpecificationData.find(item => item.Name?.trim().toLocaleLowerCase() === leadTime?.trim().toLocaleLowerCase())?.TextValue);
                if (getShippingInfo) {
                    typeOfProduct = (value.shippingInformation.trim().toLocaleLowerCase() === 'delivery') ? 'Core' : value.shippingInformation;
                    if (typeOfProduct?.toLowerCase() !== 'core') {
                        outOfStock = !!newProductSpecificationData.find(item => item.Name === outOfStockProductKey && item.BooleanValue === true);
                    }
                }
            });
            const Quantity = await this._getCartLinesData(productRecordId);
            const brandName = parseBrandName(newProductSpecificationData, this.props.context.app.config.brandBackofficeAttributePdp, productAttributesData![i].productId);
            const ItemId = getProducts![i]?.ItemId || '';
            const VariantRecordId = productAttributesData![i].productId;
            const ProductCategoryName = productAttributesData![i].categoryName;
            const zipCodeError = false;
            outOfStockSmallParcel = outOfStock;
            ///// For payment Review ///////////
            const name: string = (productAttributesData![i].Name !== undefined) ? productAttributesData![i].Name! : '';
            let dimStr = '';
            const dimStrArr: string[] = [];
            if (getProducts![i].Dimensions) {
                for (const dim of getProducts![i].Dimensions!) {
                    if (dim.DimensionTypeValue === 1 || dim.DimensionTypeValue === 3) {
                        dimStrArr.push(dim.DimensionValue?.Value ? dim.DimensionValue?.Value : '');
                    }
                }
            }
            dimStr = dimStrArr.toString();
            dimStr = dimStr.replace(',', ', ');
            const variantType: string = dimStr;
            const quantityMessage = false;
            const primaryImageUrl: string = (productAttributesData![i].PrimaryImageUrl !== undefined) ? productAttributesData![i].PrimaryImageUrl! : '';
            const fallbackImageUrl = getFallbackImageUrl(getProducts![i].ItemId, actionContext.requestContext?.apiSettings);
            const brandAndProductName = brandName ? `${brandName} ${name}` : name;
            const ProducName = name ? name : '';
            const MasterProductId = getProducts![i].MasterProductId;
            detailsProduct.push({
                typeOfProduct, outOfStock, Quantity, ItemId, VariantRecordId, zipCodeError, leadTimeVal, brandLeadTime, collection,
                outOfStockSmallParcel, name: brandAndProductName, variantType: '', primaryImageUrl: '', quantityMessage, fallbackImageUrl: '', brandName, brandAndProductName, categoryName: ProductCategoryName
            });
            localStorageProductDetails.push({
                typeOfProduct, outOfStock, Quantity, ItemId, VariantRecordId, zipCodeError, leadTimeVal, brandLeadTime, collection, ProducName, MasterProductId,
                outOfStockSmallParcel, name: brandAndProductName, variantType, primaryImageUrl, quantityMessage, fallbackImageUrl: fallbackImageUrl ? fallbackImageUrl : '', brandName
            });
        }
        this.setState({ productDetails: detailsProduct });
        getSetProductData('set', detailsProduct);
        /* VSI customization START */
        getSetProductSpec('set', localStorageProductDetails);
        this.tealiumCartDetails(detailsProduct, localStorageProductDetails);
        const categoryPath = await this.getProductsCategoryPath(this.props, detailsProduct);
        this.setState({
            productCategoryPath: categoryPath
        });
        // localStorage.setItem('product_specification', JSON.stringify(localStorageProductDetails));
        /* VSI customization END */
        return detailsProduct;
    }

    public tealiumCartDetails = async (detailsProduct: IProductDetails[], localStorageProductDetails: IProductDetails[]) => {
        const { context, data: {
            cart: { result: cart },
            productAttributes: { result: productAttributes }
        } } = this.props;

        const originUrl = this.props.context.request.url.requestUrl.origin;
        const cartState = await getCartState(context.actionContext);
        const quantity = cartState && cartState.cart.TotalItems;
        const subTotal = cartState && cartState.cart.SubtotalAmount;
        const productImageUrl: string[] = [];
        const productQuantity: string[] = [];
        const productRecordIdArray: number[] = [];
        const productURL: string[] = [];
        const productIDsArray: string[] = [];
        const productNameArray: string[] = [];
        const productSize: string[] = [];
        const productPriceArray: string[] = [];
        const cartProductPriceArray: string[] = [];
        const variantRecord: string[] = [];
        const productCatgoryArray: string[] = [];
        const productBrandName: (string | undefined)[] = [];
        const productDiscountArray: string[] = [];
        detailsProduct?.map(details => {
            productQuantity.push(`${details?.Quantity}`);
            productRecordIdArray.push(details?.VariantRecordId);
            productIDsArray.push(details?.ItemId);
            productNameArray.push(details?.brandAndProductName!);
        });
        cart?.cart.CartLines?.map((items) => {
            let productDiscount = 0.00;
            variantRecord.push(items.Barcode!);

            items.DiscountLines?.map(sales => {
                if (sales.DiscountCode) {
                    productDiscount = productDiscount + sales.EffectiveAmount!;
                }
            });
            const updatedPrice = items.NetAmountWithoutTax! + productDiscount;
            cartProductPriceArray.push(`${items.NetAmountWithoutTax}`);
            productPriceArray.push(`${updatedPrice?.toFixed(2)?.toString()}`);
            productDiscountArray.push(`${productDiscount?.toFixed(2)?.toString()}`);
        });
        localStorageProductDetails?.map(items => {
            productImageUrl.push(items?.primaryImageUrl);
            productSize.push(items?.variantType);

        });
        this.props.data.products.result?.map(item => {
            const urlProduct = this.generateURLForPDP(item, this.props.context);
            productURL.push(`${originUrl}${urlProduct}`);
            productCatgoryArray.push(`${hasArray(productAttributes)?.find(p => p?.productId === item?.RecordId)?.categoryName}`);
            const attr = hasArray(productAttributes)?.find(p => p?.productId === item?.RecordId)?.attributeValue;
            const brandName = parseBrandName(attr, this.props.context.app.config.brandBackofficeAttributePdp, item?.RecordId);
            productBrandName.push(brandName);
        });
        const cartUrl = `${originUrl}/cart?qtys=${productQuantity?.join()}&pids=${productRecordIdArray?.join()}&utm_content=MainHero`;
        const productDetailsData = {
            quantity,
            subTotal,
            productQuantity,
            productSize,
            productIDsArray,
            productNameArray,
            productPriceArray,
            variantRecord,
            productCatgoryArray,
            productBrandName,
            productDiscountArray,
            cartProductPriceArray
        };
        const cartProductUrl = { productImageUrl, productURL, cartUrl };
        setTealiumCartVariables('tealium_cart_products_details', this.props.context, productDetailsData);
        setTealiumCartVariables('tealium_cart_products_url', this.props.context, cartProductUrl);
    };

    public generateURLForPDP = (product: SimpleProduct, context: ICoreContext<IGeneric<IAny>>) => {
        let newURL = '';
        if (product) {
            const variantProductId = product.RecordId;
            newURL = getProductUrlSync(product, context.actionContext, undefined);
            if (context.app.config.queryBasedVariantSelection) {
                newURL = `${newURL}?variantid=${variantProductId}`;
            }
            return newURL;
        }
        return newURL;
    };

    public generateProductUpdateStrings = () => {
        const { data: {
            cart: { result: cart },
            productAttributes
        } } = this.props;
        const cookies = new Cookies();
        const siteCookie = cookies.get('d365_site');
        const originUrl = this.props.context.request.url.requestUrl.origin;
        const quantity = cart?.cart.TotalItems;
        const subTotal = cart?.cart.SubtotalAmount;
        const productPriceArray: string[] = [];
        const cartProductPriceArray: string[] = [];
        const productIDsArray: string[] = [];
        const productImageUrl: string[] = [];
        const productQuantity: string[] = [];
        const variantRecord: string[] = [];
        const productRecordIdArray: string[] = [];
        const productNameArray: string[] = [];
        const productURL: string[] = [];
        const productSize: string[] = [];
        const productCatgoryArray: string[] = [];
        const productBrandName: string[] = [];
        const productDiscountArray: string[] = [];
        const urlQuantity: number[] = [];
        let isCoreItem: string;
        let funnelType: string;
        const isCheckCore = this.coreProductAvailable();
        if (isCheckCore) {
            isCoreItem = "true";
            funnelType = "full funnel";
        } else {
            isCoreItem = "false";
            funnelType = "no core item";
        }
        this.props.data.products.result?.map((items) => {
            productIDsArray.push(`${items.ItemId}`);
            productImageUrl.push(`${items.PrimaryImageUrl}`);
            productRecordIdArray.push(`${items.RecordId}`);
            const attr = hasArray(productAttributes)?.find(p => p?.productId === items?.RecordId)?.attributeValue;
            const brandName = parseBrandName(attr, this.props.context.app.config.brandBackofficeAttributePdp, items?.RecordId);
            const nameWithBrand = `${brandName} ${items.Name}`;
            productNameArray.push(`${nameWithBrand.toString()}`);
            const urlProduct = this.generateURLForPDP(items, this.props.context);
            productBrandName.push(`${brandName}`);
            productURL.push(`${originUrl}${urlProduct}`);
            productCatgoryArray.push(hasArray(productAttributes)?.find(p => p?.productId === items?.RecordId)?.categoryName);
        });
        cart?.cart.CartLines?.map((items) => {
            let productDiscount = 0;

            variantRecord.push(`${items.Barcode}`);
            productQuantity.push(`${items.Quantity}`);
            urlQuantity.push(items.Quantity!);
            productSize.push(`${this.getVariant(this.getProduct(this.props.data.products.result!, items)).toString().replace(/,/g, '')}`);
            items.DiscountLines?.map(sales => {
                if (sales.DiscountCode) {
                    productDiscount = productDiscount + sales.EffectiveAmount!;
                }
            });
            const updatedPrice = items.NetAmountWithoutTax! + productDiscount;
            cartProductPriceArray.push(`${items.NetAmountWithoutTax!}`);
            productPriceArray.push(`${updatedPrice?.toFixed(2)}`);
            productDiscountArray.push(`${productDiscount?.toFixed(2)?.toString()}`);
        });
        const cartUrl = `${originUrl}/cart?qtys=${urlQuantity?.join()}&pids=${productRecordIdArray?.join()}&utm_content=MainHero`;
        const productDetailsData = {
            productPriceArray,
            productIDsArray,
            productImageUrl,
            productQuantity,
            variantRecord,
            productNameArray,
            quantity,
            subTotal,
            cartUrl,
            siteCookie,
            productURL,
            productSize,
            productCatgoryArray,
            productBrandName,
            productDiscountArray,
            cartProductPriceArray,
            isCoreItem,
            funnelType
        };

        return productDetailsData;
    };

    public generateProductData = () => {
        const { data: {
            cart: { result: cart },
            productAttributes
        } } = this.props;
        const cookies = new Cookies();
        const siteCookie = cookies.get('d365_site');
        const originUrl = this.props.context.request.url.requestUrl.origin;
        const quantity = cart?.cart.TotalItems;
        const subTotal = cart?.cart.SubtotalAmount;
        const productPriceArray: string[] = [];
        const cartProductPriceArray: string[] = [];
        const productIDsArray: string[] = [];
        const productImageUrl: string[] = [];
        const productQuantity: string[] = [];
        const variantRecord: string[] = [];
        const productRecordIdArray: string[] = [];
        const productNameArray: string[] = [];
        const productURL: string[] = [];
        const productSize: string[] = [];
        const productCatgoryArray: string[] = [];
        const productBrandName: string[] = [];
        const productDiscountArray: string[] = [];
        const urlQuantity: number[] = [];
        let isCoreItem: string;
        let funnelType: string;
        this.props.data.products.result?.map((items) => {
            productIDsArray.push(`"${items.ItemId}"`);
            productImageUrl.push(`"${items.PrimaryImageUrl}"`);
            productRecordIdArray.push(`${items.RecordId}`);
            const attr = hasArray(productAttributes)?.find(p => p?.productId === items?.RecordId)?.attributeValue;
            const brandName = parseBrandName(attr, this.props.context.app.config.brandBackofficeAttributePdp, items?.RecordId);
            const nameWithBrand = `${brandName?.replace(/'/g, "")} ${items.Name?.replace(/'/g, "")}`;
            productNameArray.push(`'${nameWithBrand.toString()}'`);
            const urlProduct = this.generateURLForPDP(items, this.props.context);
            productURL.push(`"${originUrl}${urlProduct}"`);
            productBrandName.push(`"${brandName?.replace(/'/g, "")}"`);
            productCatgoryArray.push(`"${hasArray(productAttributes)?.find(p => p?.productId === items?.RecordId)?.categoryName}"`);
        });
        cart?.cart.CartLines?.map((items) => {
            let productDiscount = 0.00;
            variantRecord.push(`"${items.Barcode}"`);
            productQuantity.push(`"${items.Quantity}"`);
            urlQuantity.push(items.Quantity!);
            productSize.push(`'${this.getVariant(this.getProduct(this.props.data.products.result!, items)).toString().replace(/,/g, '')}'`);
            items.DiscountLines?.map((sales) => {
                if (sales.DiscountCode) {
                    productDiscount = productDiscount + sales.EffectiveAmount!;
                }
            });
            const updatedPrice = items.NetAmountWithoutTax! + productDiscount;
            cartProductPriceArray.push(`"${items.NetAmountWithoutTax}"`);
            productPriceArray.push(`"${updatedPrice?.toFixed(2)}"`);
            productDiscountArray.push(`'${productDiscount?.toFixed(2)?.toString()}'`);
        });
        const isCheckCore = this.coreProductAvailable();
        if (isCheckCore) {
            isCoreItem = "true";
            funnelType = "full funnel";
        } else {
            isCoreItem = "false";
            funnelType = "no core item";
        }
        const cartUrl = `${originUrl}/cart?qtys=${urlQuantity?.join()}&pids=${productRecordIdArray?.join()}&utm_content=MainHero`;
        const productDetailsData = {
            productPriceArray,
            productIDsArray,
            productImageUrl,
            productQuantity,
            variantRecord,
            productNameArray,
            quantity,
            subTotal,
            cartUrl,
            siteCookie,
            productURL,
            productSize,
            productCatgoryArray,
            productBrandName,
            productDiscountArray,
            cartProductPriceArray,
            isCoreItem,
            funnelType
        };

        return productDetailsData;
    };


    private getProduct = (prods: SimpleProduct[], cartLine: CartLine): SimpleProduct | null => {
        for (const pr of prods) {
            if (pr.RecordId === cartLine.ProductId) {
                return pr;
            }
        }
        return null;
    };

    public getVariant = (product: SimpleProduct | null) => {
        if (product === null) {
            return '';
        }
        let dimensionValueCustom = '';
        product.Dimensions?.map((item, index) => {
            if (item.DimensionValue?.Value !== 'Prime') {
                if (index === 0) {
                    dimensionValueCustom += item.DimensionValue?.Value!;
                }
                else {
                    dimensionValueCustom += ' , ' + `${item.DimensionValue?.Value!}`;
                }
            }
        });
        return dimensionValueCustom;
    };


    private async _getCartLinesData
        (recordID: number): Promise<number> {
        const { context } = this.props;
        const cartState = await getCartState(context.actionContext);
        let quantity = 0;
        const getQuantity = cartState.cart.CartLines?.find(item => item.ProductId === recordID);
        if (getQuantity) {
            quantity = getQuantity.Quantity ? getQuantity.Quantity : 0;
        }
        return quantity;
    }
    // tslint:disable-next-line: function-name TODO
    // private getProductBrandName = (productSpec: IAttributeValueProduct[]): string => {
    //     const { context } = this.props;
    //     let brandName = '';
    //     productSpec?.map((item) => {
    //         if (item.Name?.toLowerCase() === context.app.config.brandBackofficeAttributePdp?.toLowerCase()) {
    //             brandName = item.TextValue!;
    //         }
    //     });
    //     return brandName;
    // };

    // tslint:disable-next-line:function-name max-line-length cyclomatic-complexity
    private async checkSmallParcelOutOfStock(detailsProduct: IProductDetails[], context: ICoreContext<IGeneric<IAny>>): Promise<IProductDetails[]> {
        const cookies = new Cookies();
        const zipcode = cookies.get('zip_code');
        const date = new Date();
        const currentDate = date.setDate(date.getDate());
        const tempProducts: { ItemId: string; Quantity: number; VariantRecordId: string }[] = [];
        // Store small parcel products with their location ids
        const smallParcelLocations: IProductLocationDetails[] = [];

        detailsProduct?.forEach((item) => {
            if (item?.typeOfProduct === 'Parcel' && !item?.outOfStockSmallParcel) {
                tempProducts.push({
                    ItemId: item.ItemId,
                    Quantity: 1, // item.Quantity,
                    VariantRecordId: item.VariantRecordId?.toString()
                });
            }
        });
        if (tempProducts?.length) {
            const requestBody = {
                InventoryType: 'Delivery',
                StoreId: '',
                Weeks: context.app && context.app.config.weeksPDPforATPCall,
                RequestedDate: dayjs(currentDate).format('MM/DD/YYYY'),
                ZipCode: zipcode, // '77047',
                ItemLines: tempProducts,
                NoOfPriorityDC: context.app.config.NoOfPriorityDC ? context.app.config.NoOfPriorityDC : 0
            };
            const result = await _getInventoryQuantity(requestBody, context, false);
            detailsProduct.forEach((prod) => {
                result?.ATPInventoryStatusData?.forEach((item) => {
                    if (item && prod && (prod.VariantRecordId?.toString() === item.VariantRecordId?.toString())) {
                        prod.outOfStock = item.Available === 'NO';
                        prod.InventLocationId = item.Location;
                        prod.deliveryTime = item.Date?.trim().substring(10);
                        const numberQuantity = item.Quantity;
                        if (numberQuantity) {
                            prod.availableQuantity = +numberQuantity;
                        }
                        // Check small parcel locations to update in cartLines
                        const smallParcelDetails: IProductLocationDetails = {
                            itemId: item.ItemId!,
                            productId: item.VariantRecordId,
                            location: item.Location!
                        };
                        smallParcelLocations.push(smallParcelDetails);

                    }
                });
            });

            if (result && result.ATPInventoryStatusData && result.ATPInventoryStatusData.length) {
                this.setState({ smallParcelLocationDetails: smallParcelLocations }); // Set small parcel location to be used in updateCart api call to set location
                for (const invData of result.ATPInventoryStatusData) {
                    if (this.props.data.cart.result?.cart.CartLines && this.props.data.cart.result?.cart.CartLines.length) {
                        for (const cLin of this.props.data.cart.result?.cart.CartLines) {
                            const pId = cLin.ProductId;
                            if (pId?.toString() === invData.VariantRecordId) {
                                const invQuantity = (invData.Quantity && invData.Quantity?.length) ? parseInt(invData.Quantity, 10) : 0;
                                if (cLin.Quantity && (cLin.Quantity > invQuantity) && invData.Available !== 'NO') {
                                    await this._updateCartlineQuantityWithAvailableQuantity(cLin, invQuantity);
                                }
                            }
                        }
                    }
                }
            }
        }
        if (detailsProduct && detailsProduct.length > 0) {
            this.setState({
                productSpecifications: detailsProduct,
                productDetails: detailsProduct
            });
            getSetProductData('set', detailsProduct);
        }
        detailsProduct && detailsProduct.length > 0 && this.renderDeliveryMessage(detailsProduct);
        return detailsProduct;
    }

    // tslint:disable: max-func-body-length
    private renderDeliveryMessage = (collectiveProductDetails: IProductDetails[]) => {
        const { context } = this.props;
        const parcelDropShipProducts: IProductDetails[] = [];
        const allDropParceldMsgs: ICoreDeliveryByMessage[] = [];
        const newSPDS_Att: ICoreDeliveryByMessage[] = [];
        let smallParcelLatestDay: Dayjs;
        let dropShipLatestDay: Dayjs;
        let lastSmallParcelDay: Dayjs;
        let lastDropShipDay: Dayjs;
        const cookies = new Cookies();
        collectiveProductDetails?.map((item, index) => {
            if (item.typeOfProduct.toLowerCase() === 'parcel' || item.typeOfProduct.toLowerCase() === 'drop ship') {
                parcelDropShipProducts.push(item);
            }
        });
        // tslint:disable: cyclomatic-complexity
        parcelDropShipProducts?.map((itemsMessage) => {
            const deliveryDays = itemsMessage.leadTimeVal || itemsMessage.brandLeadTime;
            if (!itemsMessage.outOfStock && deliveryDays !== undefined && deliveryDays !== '') {
                let deliveryByMessage = '';
                let deliveryByMessageRange: string = '';
                let date = new Date();
                const daysSpan = deliveryDays?.split('-');
                const deliveryDate = date.setDate(date.getDate());
                if (daysSpan && daysSpan[1] !== undefined && daysSpan.length) {
                    daysSpan.map((item: string, index: number) => {
                        if (parseInt(item, 10) > 0) {
                            let numberOfDays = parseInt(item, 10);
                            let newDate = deliveryDate;
                            date = new Date();
                            while (numberOfDays > 0) {
                                newDate = date.setDate(date.getDate() + 1);
                                if (dayjs(newDate).format('d') !== '0' && dayjs(newDate).format('d') !== '6') {
                                    numberOfDays--;
                                }
                            }
                            deliveryByMessage = dayjs(newDate).format('MMM D');
                            if (deliveryByMessageRange !== '') {
                                deliveryByMessageRange = `${deliveryByMessageRange} - ${deliveryByMessage}`;
                            } else {
                                deliveryByMessageRange = deliveryByMessage;
                                newDate = date.setDate(date.getDate() - parseInt(item[0], 10));
                            }
                            ///// for payment review //////////
                            if (itemsMessage.typeOfProduct.toLowerCase() === 'parcel') {
                                if (smallParcelLatestDay === undefined || smallParcelLatestDay === null) {
                                    smallParcelLatestDay = dayjs(newDate);
                                } else if (dayjs(newDate) > smallParcelLatestDay) {
                                    smallParcelLatestDay = dayjs(newDate);
                                }
                            } else if (itemsMessage.typeOfProduct.toLowerCase() === 'drop ship') {
                                if (dropShipLatestDay === undefined || dropShipLatestDay === null) {
                                    dropShipLatestDay = dayjs(newDate);
                                } else if (dayjs(newDate) > dropShipLatestDay) {
                                    dropShipLatestDay = dayjs(newDate);
                                }
                            }
                            ///// for payment review //////////
                        }
                    });
                    deliveryByMessage = deliveryByMessageRange;
                    ///// for payment review //////////
                    if (itemsMessage.typeOfProduct.toLowerCase() === 'parcel') {
                        if (lastSmallParcelDay === undefined || lastSmallParcelDay === null) {
                            lastSmallParcelDay = smallParcelLatestDay;
                            context.actionContext.update(new CartDataInfoInput(), { delMessageParcelLatest: deliveryByMessage });
                            cookies.set(`payment_review_parcel_latest_del_msg`, deliveryByMessage, { path: '/' });
                        } else if (lastSmallParcelDay < smallParcelLatestDay) {
                            lastSmallParcelDay = smallParcelLatestDay;
                            context.actionContext.update(new CartDataInfoInput(), { delMessageParcelLatest: deliveryByMessage });
                            cookies.set(`payment_review_parcel_latest_del_msg`, deliveryByMessage, { path: '/' });
                        }
                    } else if (itemsMessage.typeOfProduct.toLowerCase() === 'drop ship') {
                        if (lastDropShipDay === undefined || lastDropShipDay === null) {
                            lastDropShipDay = dropShipLatestDay;
                            context.actionContext.update(new CartDataInfoInput(), { delMessageDropShipLatest: deliveryByMessage });
                            cookies.set(`payment_review_dropship_latest_del_msg`, deliveryByMessage, { path: '/' });
                        } else if (lastDropShipDay < dropShipLatestDay) {
                            lastDropShipDay = dropShipLatestDay;
                            context.actionContext.update(new CartDataInfoInput(), { delMessageDropShipLatest: deliveryByMessage });
                            cookies.set(`payment_review_dropship_latest_del_msg`, deliveryByMessage, { path: '/' });
                        }
                    }
                    ///// for payment review //////////
                    allDropParceldMsgs.push({
                        itemID: itemsMessage.ItemId,
                        variantID: itemsMessage.VariantRecordId.toString(),
                        deliveryByMessage: `Delivery by ${deliveryByMessage}`,
                        daySpan: deliveryDays
                    });
                    newSPDS_Att.push({
                        itemID: itemsMessage.ItemId,
                        variantID: itemsMessage.VariantRecordId.toString(),
                        deliveryByMessage: `${deliveryByMessage}`,
                        daySpan: deliveryDays
                    });
                } else {
                    deliveryByMessage = this._deliveryMessage(deliveryDays);
                    ///// for payment review //////////
                    if (itemsMessage.typeOfProduct.toLowerCase() === 'parcel') {
                        if (smallParcelLatestDay === undefined || smallParcelLatestDay === null) {
                            smallParcelLatestDay = dayjs(deliveryDays);
                        } else if (dayjs(deliveryDays) > smallParcelLatestDay) {
                            smallParcelLatestDay = dayjs(deliveryDays);
                        }
                    } else if (itemsMessage.typeOfProduct.toLowerCase() === 'drop ship') {
                        if (dropShipLatestDay === undefined || dropShipLatestDay === null) {
                            dropShipLatestDay = dayjs(deliveryDays);
                        } else if (dayjs(deliveryDays) > dropShipLatestDay) {
                            dropShipLatestDay = dayjs(deliveryDays);
                        }
                    }

                    if (itemsMessage.typeOfProduct.toLowerCase() === 'parcel') {
                        if (lastSmallParcelDay === undefined || lastSmallParcelDay === null) {
                            lastSmallParcelDay = smallParcelLatestDay;
                            context.actionContext.update(new CartDataInfoInput(), { delMessageParcelLatest: deliveryByMessage });
                            cookies.set(`payment_review_parcel_latest_del_msg`, deliveryByMessage, { path: '/' });
                        } else if (lastSmallParcelDay < smallParcelLatestDay) {
                            lastSmallParcelDay = smallParcelLatestDay;
                            context.actionContext.update(new CartDataInfoInput(), { delMessageParcelLatest: deliveryByMessage });
                            cookies.set(`payment_review_parcel_latest_del_msg`, deliveryByMessage, { path: '/' });
                        }
                    } else if (itemsMessage.typeOfProduct.toLowerCase() === 'drop ship') {
                        if (lastDropShipDay === undefined || lastDropShipDay === null) {
                            lastDropShipDay = dropShipLatestDay;
                            context.actionContext.update(new CartDataInfoInput(), { delMessageDropShipLatest: deliveryByMessage });
                            cookies.set(`payment_review_dropship_latest_del_msg`, deliveryByMessage, { path: '/' });
                        } else if (lastDropShipDay < dropShipLatestDay) {
                            lastDropShipDay = dropShipLatestDay;
                            context.actionContext.update(new CartDataInfoInput(), { delMessageDropShipLatest: deliveryByMessage });
                            cookies.set(`payment_review_dropship_latest_del_msg`, deliveryByMessage, { path: '/' });
                        }
                    }
                    newSPDS_Att.push({
                        itemID: itemsMessage.ItemId,
                        variantID: itemsMessage.VariantRecordId.toString(),
                        deliveryByMessage: `${deliveryByMessage}`,
                        daySpan: deliveryDays
                    });
                    ///// for payment review //////////
                    allDropParceldMsgs.push({
                        itemID: itemsMessage.ItemId,
                        variantID: itemsMessage.VariantRecordId.toString(),
                        deliveryByMessage: `Delivery by ${deliveryByMessage}`,
                        daySpan: deliveryDays
                    });
                }
            }
        });
        if (allDropParceldMsgs && allDropParceldMsgs.length > 0) {
            this.setState({ deliveryByMessageSPDS: allDropParceldMsgs });
        }
        if (newSPDS_Att && newSPDS_Att.length > 0) {
            const newProductDetails: IProductDetails[] = [];
            this.state.productDetails?.map((items) => {
                const getDateDetails = newSPDS_Att?.find(data => Number(data?.variantID) === Number(items?.VariantRecordId));
                if (getDateDetails) {
                    let currentYear = dayjs().year();
                    const datedValues = items?.leadTimeVal?.split('-') || [];
                    if (datedValues?.length > 1) {
                        const numberOfDays = Number(datedValues[0]) + Number(datedValues[1]);
                        currentYear = dayjs().add(numberOfDays, 'd').year();
                    } else if (datedValues?.length === 1) {
                        currentYear = dayjs().add(Number(datedValues[0]), 'd').year();
                    }
                    const date = getDateDetails?.deliveryByMessage?.split('-');
                    if (date?.length > 1) {
                        const getDate = dayjs(`${date[1]} ${currentYear}`).format('MM/DD/YYYY');
                        const fullDate = `${getDate} ${items?.deliveryTime ? items?.deliveryTime : '12:00:00 AM'}`;
                        const formatDeliveryDate = dayjs(fullDate).format('YYYY-MM-DDTHH:mm:ss');
                        items.DeliveryDate = formatDeliveryDate;
                        items.RequestedDeliveryDate = fullDate;
                    } else {
                        const getDate = dayjs(`${date[0]} ${currentYear}`).format('MM/DD/YYYY');
                        const fullDate = `${getDate} ${items?.deliveryTime ? items?.deliveryTime : '12:00:00 AM'}`;
                        const formatDeliveryDate = dayjs(fullDate).format('YYYY-MM-DDTHH:mm:ss');
                        items.DeliveryDate = formatDeliveryDate;
                        items.RequestedDeliveryDate = fullDate;
                    }
                }
                newProductDetails.push(items);
            });
            getSetProductData('set', newProductDetails);
        }
    };

    private _deliveryMessage = (days: string) => {
        const date = new Date();
        const deliveryDate = date.setDate(date.getDate());
        let deliveryByMessage = '';
        deliveryByMessage = dayjs(deliveryDate).format('MMM D');
        let numberOfDays = parseInt(days, 10);
        let newDate = deliveryDate;
        let flag = true;
        while (numberOfDays > 0) {
            newDate = date.setDate(date.getDate() + 1);
            if (dayjs(newDate).format('d') !== '0' && dayjs(newDate).format('d') !== '6') {
                numberOfDays--;
            } else {
                flag = false;
            }
        }
        deliveryByMessage = (flag && days === '1') ? `Tomorrow ${dayjs(date.setDate(date.getDate())).format('MMM D')}` : dayjs(newDate).format('ddd, MMM D');
        return deliveryByMessage;
    };

    // tslint:disable-next-line:function-name
    private async checkCoreProductErrorZipCode(detailsProduct: IProductDetails[]): Promise<IProductDetails[]> {
        const { oomZipCode } = this.state;
        detailsProduct?.forEach((item) => {
            if (item?.typeOfProduct === 'Core') {
                if (oomZipCode) {
                    item.zipCodeError = true;
                    item.outOfStock = false;
                } else {
                    item.zipCodeError = false;
                }
            }
        });
        return detailsProduct;
    }
    // tslint:disable-next-line: function-name
    private async getProductsCategoryPath(props: IMfrmCartProps<IMfrmCartData>, detailsProduct: IProductDetails[]): Promise<CategoryPathResult[]> {
        const {
            data: {
                products: { result: products }
            },
            app: { config },
            context: {
                request: {
                    apiSettings: {
                        catalogId
                    }
                }
            }
        } = props;
        const getProducts: SimpleProduct[] = products || [];
        const getProductIdArray = [];
        for (let i = 0; i < getProducts?.length; i++) {
            getProductIdArray.push(getProducts[i].RecordId);
        }
        const productsCategories = await getProductsCategoryPathsAction(new GetCategoryPathsInput(props.context.request, catalogId, getProductIdArray), props.context.actionContext);
        const getSpecifications = detailsProduct;
        const revenueCategoryConfig = this.props.app.config.revenueCategory;
        const newProductSpecification = getSpecifications?.map((item) => {
            const checkCategory = productsCategories.find(data => data.ProductId === item.VariantRecordId);
            const getSegments = checkCategory && checkCategory.CategoryPath && checkCategory.CategoryPath;
            let stopProccess = false;
            getSegments?.forEach((path) => {
                path && path.Segments && path.Segments?.forEach((itemName) => {
                    const checkExist = revenueCategoryConfig.find((revenueItemName: IRevenueDetails) => itemName?.CategoryName?.toLowerCase()?.trim() === revenueItemName?.CategoryName?.toLowerCase()?.trim());
                    if (checkExist) {
                        stopProccess = true;
                        item.revenueCategory = checkExist?.Name;
                        item.categoryName = checkExist?.CategoryName;
                    } else {
                        if (itemName?.CategoryName === this.props.resources.mattresses
                            || itemName?.CategoryName === this.props.resources.adjustableBases
                            || itemName?.CategoryName === this.props.resources.boxSprings
                            || itemName?.CategoryName === this.props.resources.pillows
                            || itemName?.CategoryName === this.props.resources.babyAndKids) {
                            item.categoryName = itemName?.CategoryName;
                        }
                        if (!stopProccess) {
                            item.revenueCategory = config.otherRevenueDetail;
                        }
                    }
                });
            });
            return item;
        });
        this.setState({ productDetails: newProductSpecification, productSpecifications: newProductSpecification });
        getSetProductData('set', newProductSpecification);
        return productsCategories?.filter(Boolean);
    }

    // tslint:disable-next-line: no-any
    /* eslint-disable @typescript-eslint/no-floating-promises */
    private _giftCart = (cartlineTo: CartLine, context: any) => {
        this._showCtaGiftProducts(cartlineTo, context, this.props.data.cart.result?.cart.CartLines);
    };

    private promotionCloseButtonclick = () => {
        this._hidePromotionDialog();
    };

    private _hidePromotionDialog(): void {
        this.setState({
            showPromotionDialog: false
        });
    }

    /*============================Zip-Code merging=================================*/
    private _renderUpdateZipCode = (): JSX.Element => {
        const {
            context: {
                app: { config: findStoreRedirectLink }
            }
        } = this.props;
        // const cookies = new Cookies();
        // const zipcode = cookies.get('zip_code');
        // const defaultZipCode = geoLocation?.zipCode || '';
        // this.setState({newZipCode:zipcode || defaultZipCode});
        const findstoreLink = findStoreRedirectLink ? findStoreRedirectLink : '';
        const _onChangeInput = (event: React.ChangeEvent<HTMLInputElement>): void => {
            const pattern = /[^0-9]/g;
            if (!pattern.test(event.target.value)) {
                this.setState({ newZipCode: event.target.value, showError: false });
                // setNewZipCode(event.target.value);
                // setShowError(false);
            }
        };

        const _onFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (this._updateZipCode(this.state.newZipCode, this.props, this.state.errorZipCode, this.state.showError)) {
                // this.setState({isInput:!this.state.isInput ? true:false});
                // setIsInput(prevState => !prevState);
            }
        };

        const _showZipCodeInput = () => {
            this.setState({ isInput: !this.state.isInput ? true : false, newZipCode: this.state.newZipCode });
            // setIsInput(prevState => !prevState);
            // setNewZipCode(newZipCode);
        };
        const _onFocusInput = (event: React.FocusEvent<HTMLInputElement>) => {
            event.currentTarget.select();
        };

        const addSpacesZipCode = (zipCode: string) => {
            return zipCode?.toString().split('').join(' ');
        };
        return (
            <Node className={this.state.isInput ? 'msc-cart__delivery-message msc-cart__delivery-message--input-active' : 'msc-cart__delivery-message'}>
                {/* VSI Customization {52133} */}
                <Node id='DeliveryLabel' className='msc-cart__delivery-message-text' tag='span' tabIndex={0}>
                    <span tabIndex={-1}>
                        <span className='sr-only'>{`${this.props.resources.cartDeliveryText} ${addSpacesZipCode(this.state.showZipCodeDelivery)}`}</span>
                        <span aria-hidden='true'>{this.props.resources.cartDeliveryText} {this.state.showZipCodeDelivery}</span>
                    </span>
                </Node>
                {this.state.isInput ?
                    <>
                        <Node tag='form' onSubmit={_onFormSubmit} className='msc-cart__delivery-message-input field-group' id='goTo'>
                            <input
                                type='text'
                                pattern='\d*'
                                className={`field-control ${this.state.newZipCode && 'field-control--value'} ${this.state.showError && 'field-control--error'}`}
                                value={this.state.newZipCode.replace(/[^0-9]/g, '')}
                                maxLength={5}
                                autoFocus
                                onFocus={_onFocusInput}
                                onChange={_onChangeInput}
                                aria-invalid={this.state.showError ? 'true' : 'false'}
                                inputMode='numeric'
                            />
                            <Node className='field-label' tag='label'>Zip Code</Node>
                            <Node
                                className='msc-cart__delivery-message-button'
                                tag='button'
                                role='button'
                                type='submit'
                            >
                                {this.props.resources.updateZipCodeButtonText}
                            </Node>
                        </Node>
                        {this.state.showError ? <Node className='msc-cart__delivery-message-text-error' role='alert'>
                            {this.props.resources.cartInvalidZipCode}
                        </Node> : <></>}
                    </>
                    :
                    <>
                        <Node
                            className='msc-cart__delivery-message-link'
                            tag='button'
                            role='button'
                            tabIndex={0}
                            onClick={_showZipCodeInput}
                        >
                            {this.props.resources.changeZipCodeButtonText}
                            <Node tag='span' className='sr-only'>{addSpacesZipCode(this.state.showZipCodeDelivery)}</Node>
                        </Node>
                        {/* VSI Customization {52133} End*/}
                        {this.state.errorZipCode !== '' ? <div className='msc-cart__delivery-message-text-error' role='alert'><RichTextComponent text={this.state.errorZipCode} /></div> : <></>}
                        {this.state.showFindStore && <Node className='msc-cart__find-store-text'>
                            <a href={`${findstoreLink}${this.state.newZipCode}`} target='_blank'>{this.props.resources.findStoreText}</a>
                        </Node>}
                    </>
                }
            </Node >
        );
    };

    private _getStateFromZipCode = (props: IMfrmCartProps<IMfrmCartData>, zipcode: string, disabled: boolean) => {
        disabled && this.props.context.actionContext.update(new PromoCodeAppliedOnCartInput(), { isInProcess: true });
        const { telemetry, resources } = this.props;
        if (!hasArray(this.props.data.cart.result?.cart?.CartLines) || !zipcode) {
            if (!zipcode) {
                this.props.context.actionContext.update(new PromoCodeAppliedOnCartInput(), { isInProcess: true });
                this.setState({ errorZipCode: resources.cartInvalidZipCode, showError: true, isInput: true });
            }
            return;
        }
        this.setState({ errorZipCode: '', disableCheckoutButton: false, showFindStore: false, oomZipCode: false });
        // disableCheckoutButton = false;
        getAddressFromZipCodeAsync({ callerContext: props.context.actionContext }, 'USA', zipcode)
            .then(async zipcodes => {
                if (zipcodes && zipcodes.length) {
                    const stateObj = zipcodes[0];
                    const cookies = new Cookies();
                    cookies.set(`zip_code`, zipcode, { path: '/', expires: oneYearFromNow() });
                    this.setState({ isInput: false, showZipCodeDelivery: zipcode });
                    const getMessageValue = getBlockZipCodeMessage(stateObj.StateId || '', props.context.app.config.blockedStates);
                    if (getMessageValue && getMessageValue.length > 0) {
                        this.setState({ errorZipCode: getMessageValue[0].message, disableCheckoutButton: true });
                        if (getMessageValue && getMessageValue[0].hasStore) {
                            this.setState({ showFindStore: true });
                        }
                        const result = this.checkCoreProductErrorZipCode(this.state.productDetails);
                        result.then((products) => {
                            this.setState({
                                productSpecifications: products,
                                productDetails: products,
                                showDeliveryStepper: false
                            });
                            getSetProductData('set', products);
                            this.props.context.actionContext.update(new PromoCodeAppliedOnCartInput(), { isInProcess: false });
                        }).catch((error) => {
                            this.props.context.actionContext.update(new PromoCodeAppliedOnCartInput(), { isInProcess: false });
                            telemetry.warning(error);
                            telemetry.debug('Unable to Check core product zip code when change zip code');
                        });
                        // disableCheckoutButton = true;
                    } else {
                        await this.checkSmallParcelOutOfStock(this.state.productDetails, props.context);
                        this._updateATPMessagesForCoreProducts().then(async res => {
                            const cartState = await getCartState(props.context.actionContext);
                            if (cartState && cartState?.cart.CartLines?.length! > 0 && this.props.context.app.config.calculateTaxOnCart) {
                                await this._getUpdatedTaxValue(this.props, stateObj.CityName, stateObj.StateId, stateObj.CountryRegionId);
                            }
                        });
                        this.props.context.actionContext.update(new PromoCodeAppliedOnCartInput(), { isInProcess: false });
                        // this.getProductsSpecifications(this.props);
                    }
                } else if (zipcodes && zipcodes.length === 0) {
                    this.props.context.actionContext.update(new PromoCodeAppliedOnCartInput(), { isInProcess: false });
                    this.setState({ errorZipCode: resources.cartInvalidZipCode, showError: true, isInput: true });
                }
            })
            .catch((error) => {
                this.props.context.actionContext.update(new PromoCodeAppliedOnCartInput(), { isInProcess: false });
                telemetry.warning(error);
                telemetry.debug('Unable to getAddressFromZipCodeAsync when change zip code');
            });
    };

    private _getUpdateLinbeDeliveryCall = (props: IMfrmCartProps<IMfrmCartData>, zipcode: string, setErrorZipCode: string, typeProduct: string) => {
        const { telemetry, resources } = this.props;
        this.setState({ errorZipCode: '', disableCheckoutButton: false, showFindStore: false, oomZipCode: false });
        // disableCheckoutButton = false;
        getAddressFromZipCodeAsync({ callerContext: props.context.actionContext }, 'USA', zipcode)
            .then(async zipcodes => {
                if (zipcodes && zipcodes.length) {
                    const stateObj = zipcodes[0];
                    const cookies = new Cookies();
                    cookies.set(`zip_code`, zipcode, { path: '/', expires: oneYearFromNow() });
                    this.setState({ isInput: false, showZipCodeDelivery: zipcode });
                    const getMessageValue = getBlockZipCodeMessage(stateObj.StateId || '', props.context.app.config.blockedStates);
                    if (getMessageValue && getMessageValue.length > 0) {
                        this.setState({ errorZipCode: getMessageValue[0].message, disableCheckoutButton: true });
                        if (getMessageValue && getMessageValue[0].hasStore) {
                            this.setState({ showFindStore: true });
                        }
                        const result = this.checkCoreProductErrorZipCode(this.state.productDetails);
                        result.then((products) => {
                            this.setState({
                                productSpecifications: products,
                                productDetails: products,
                                showDeliveryStepper: false
                            });
                            getSetProductData('set', products);
                        }).catch((error) => {
                            telemetry.warning(error);
                            telemetry.debug('Unable to checkCoreProductErrorZipCode when cart update');
                        });
                        // disableCheckoutButton = true;
                    } else {
                        const cartState = await getCartState(props.context.actionContext);
                        if (cartState && cartState?.cart.CartLines?.length! > 0 && this.props.context.app.config.calculateTaxOnCart) {
                            await this._getUpdatedTaxValue(this.props, stateObj.CityName, stateObj.StateId, stateObj.CountryRegionId);
                        }
                        if (typeProduct.toLowerCase() === 'parcel') {
                            this.renderDeliveryMessage(this.state.productDetails);
                            // this.getProductsSpecifications(this.props);
                        }
                    }
                } else if (zipcodes && zipcodes.length === 0) {
                    this.setState({ errorZipCode: resources.cartInvalidZipCode, showError: true, isInput: true });
                }
            })
            .catch((error) => {
                telemetry.warning(error);
                telemetry.debug('Unable to getAddressFromZipCodeAsync when cart update');
            });
    };

    private _updateZipCode = (newZipCode: string, props: IMfrmCartProps<IMfrmCartData>, setErrorZipCode: string, setShowError: boolean) => {
        if (newZipCode.length === 5) {
            this._getStateFromZipCode(props, newZipCode, true);
            this.setState({ newZipCode: newZipCode });

            return true;
        }
        this.setState({ showError: true });
        // setShowError(true);
        return false;
    };

    private _getUpdatedTaxValue = async (props: IMfrmCartProps<IMfrmCartData>, cityName: string | undefined, StateId: string | undefined, threeLetterISOId: string | undefined) => {
        const {
            data: { cart: result },
            context: {
                app: { config }
            },
            telemetry
        } = props;
        const { deliveryMessageManagementValues, TaxGroup, RecycleFeeAttribute } = config;
        const cartState = await getCartState(props.context.actionContext);
        const cartData = (await result)?.cart;
        const cookies = new Cookies();
        let typeOfProduct: string = '';
        const cartId = (await result)?.cart?.Id;
        const zipCode = cookies.get('zip_code');
        const lineDeliverySpecificationValue: LineDeliverySpecification[] = [];
        const { deliveryServices } = this.props.app.config;
        const newCartLineData: CartLine[] = [];
        (await result)?.cart?.CartLines?.map((item) => {
            const checkExist = !!deliveryServices.find((val: { serviceProductRecId: number }) => val.serviceProductRecId === item.ProductId);
            if (!checkExist) {
                newCartLineData.push(item);
            }
        });
        const productspecificationsDetails: IProductDetails[] = getSetProductData('get');
        const productTypesSpec = this.state.productDetails.length > 0 ? this.state.productDetails : productspecificationsDetails;

        newCartLineData?.map((lineItems, index) => {
            productTypesSpec?.map((prductItems) => {
                if (lineItems.ProductId === prductItems.VariantRecordId) {
                    const checkType = deliveryMessageManagementValues.find((item: { shippingInformation: string }) => item.shippingInformation === prductItems.typeOfProduct);
                    typeOfProduct = prductItems.typeOfProduct === 'Core' ? 'Delivery' : checkType?.MOD;
                }
            });
            lineDeliverySpecificationValue.push({
                LineId: lineItems.LineId,
                DeliverySpecification: {
                    DeliveryModeId: typeOfProduct,
                    DeliveryPreferenceTypeValue: 1,
                    DeliveryAddress: {
                        Name: '',
                        AddressTypeValue: 6,
                        City: cityName,
                        State: StateId,
                        Street: '',
                        ZipCode: zipCode,
                        ThreeLetterISORegionName: threeLetterISOId,
                        TaxGroup: TaxGroup
                    }
                }
            });
        });
        if (cartId) {
            try {
                const deliverySpecification = await cartOperationDoRetry(() => updateCartLineDeliverySpecificationsInternal(cartData, lineDeliverySpecificationValue, this.props.context.actionContext), cartState);
                if (config.enableCheckoutSalesTaxSolution) {
                    await this._updateCartLineWarehouseDetails();
                }
                const fee = deliverySpecification?.ExtensionProperties?.find((item) => item.Key?.toLowerCase() === RecycleFeeAttribute?.toLowerCase())?.Value?.DecimalValue!;
                cookies.set(`recycleFee`, fee);
                this.props.context.actionContext.update(new GetRecycleFeeCheckoutInput(), { recycleFee: Number(fee), tax: Number(deliverySpecification?.TaxAmount) });
                this.setState({
                    mattressRecyclingFee: props.context.cultureFormatter.formatCurrency(fee)
                });
            } catch (error) {
                telemetry.warning(error);
                telemetry.debug('Unable to updateCartLineDelivery to get updated tax');
                /* eslint-disable @typescript-eslint/no-floating-promises */

                this.setState({ mattressRecyclingFee: props.resources.yetToBeCalculatedText });
            }
        }
    };

    private _displayAddCartToTemplate(): boolean {
        const RetailB2BEcommerceFeatureState = this.props.data.featureState.result?.find(featureState => featureState.Name === 'Dynamics.AX.Application.RetailB2BEcommerceFeature');
        if (RetailB2BEcommerceFeatureState?.IsEnabled && this.props.data.customerInformation.result?.IsB2b && this.props.data.orderTemplates.result &&
            this.props.data.cart.result?.cart?.CartLines?.length && this.props.data.cart.result.cart.CartLines.length > 0 &&
            this.props.data.customerInformation.result) {
            return true;
        }

        return false;
    }

    private _getPromotions(): React.ReactNode {
        return (
            this.props.config.showAvailablePromotions &&
            cartPromotions({
                id: this.promotionPopupId,
                showPromotionPopup: this.state.showPromotionDialog,
                onSeeAllClick: this.promotionLinkClick,
                onCloseButtonClick: this.promotionCloseButtonclick,
                promotions: this.state.promotionData.promotionOptions,
                resources: {
                    promotionStringHeading: this.props.resources.promotionStringHeading,
                    promotionLinkText: this.props.resources.promotionLinkText,
                    promotionPopupHeading: this.props.resources.promotionPopupHeading,
                    promotionCloseButtonAriaLabel: this.props.resources.promotionCloseButtonAriaLabel
                }
            })
        );
    }

    private _getOrderSummaryErrors(cartLines: ICartlinesViewProps[] | null): IOrderSummaryErrors | undefined {
        const allErrors = cartLines && cartLines.map((cartline, i) => {
            // check if the product is service or not by product type
            const PRODUCTASSERVICE = 2 as ReleasedProductType.Service;
            const isServiceItem = cartline.data?.product?.ItemTypeValue === PRODUCTASSERVICE;
            if (!isServiceItem && cartline.error) {
                const fullError = (cartline.data && cartline.data.product?.Name) ? `${cartline.data.product.Name}: ${cartline.error}` : cartline.error;

                return (
                    <div className='msc-alert__line' key={i}>{fullError}</div>
                );
            }

            return null;
        }).filter((line): line is JSX.Element => !!line) || [];

        return {
            Wrapper: {
                tag: Alert,
                className: 'msc-alert',
                isOpen: allErrors.length > 0,
                color: 'danger',
                assertive: true,
                'aria-label': this.props.resources.outOfStockErrorMessageHeader
            },
            header: (
                <div className='msc-alert__header' aria-hidden='true'>
                    <span className='msi-exclamation-triangle' />
                    <span>{this.props.resources.outOfStockErrorMessageHeader}</span>
                </div>
            ),
            errors: allErrors
        };
    }

    private deliveryFee = (cartLines: CartLine[] | undefined, deliveryOptions: IDeliveryProps[]): number => {
        let deliveryFee: number | undefined = 0;
        cartLines?.map((item) => {
            const checkExist = deliveryOptions?.find(item2 => item.ProductId === item2.serviceProductRecId);
            if (checkExist) {
                deliveryFee = item.Price;
            }
        });
        return deliveryFee;
    };
    private _getOrderSummaryLineItems(): IOrderSummaryLines | undefined {
        const {
            data: {
                cart: { result: cart },
                channelDeliveryOptionConfig
            },
            telemetry,
            context,
            resources
        } = this.props;
        // this.refreshCartForTax();
        const channelConfiguration = context && context.request && context.request.channel;
        const multiplePickupStoreSwitchName = 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature';
        const featureState = this.props?.data?.featureState;
        const retailMultiplePickUpOptionEnabled = featureState?.result?.find(item => item.Name === multiplePickupStoreSwitchName)?.IsEnabled;
        const DeliveryFee = this.deliveryFee(cart?.cart.CartLines, this.props.app.config?.deliveryServices);
        return channelConfiguration && ArrayExtensions.hasElements(cart?.cart.CartLines) ?
            (
                OrderSummary({
                    cart: cart!.cart,
                    subTotalLabel: resources.itemsWithCountLabel,
                    shippingLabel: resources.estimatedShippingLabel,
                    otherChargeLabel: resources.otherCharges,
                    mattressRecyclingFee: { label: resources.mattressRecyclingFeeLabel, value: this.state.mattressRecyclingFee },
                    deliveryFee: { label: resources.deliveryFeeLabel, value: DeliveryFee },
                    taxLabel: `${resources.estimatedTaxLabel} - ${this.state.showZipCodeDelivery}`,
                    orderTotalLabel: resources.orderTotal,
                    totalDiscountsLabel: resources.totalDiscountsLabel,
                    context: this.props.context,
                    typeName: this.props.typeName,
                    id: this.props.id,
                    toBeCalculatedText: resources.yetToBeCalculatedText,
                    freeText: resources.priceFree,
                    channelConfiguration: channelConfiguration,
                    retailMultiplePickUpOptionEnabled: retailMultiplePickUpOptionEnabled,
                    channelDeliveryOptionConfig: channelDeliveryOptionConfig?.result,
                    telemetry: telemetry
                })
            ) : undefined;
    }

    private _getPromoCode(): React.ReactNode {
        const {
            data: {
                cart: { result: cart }
            },
            resources
        } = this.props;

        return cart && (
            <PromoCodeComponent
                cart={cart}
                promoCodeHeadingText={resources.promoCodeHeadingText}
                promoPlaceholderText={resources.promoPlaceholderText}
                promoCodeApplyButtonText={resources.promoCodeApplyButtonText}
                appliedPromoCodeHeadingText={resources.appliedPromoCodeHeadingText}
                collapseTimeOut={350}
                removePromoAriaLabelFormat={resources.removePromoAriaLabelFormat}
                removePromoText={resources.removePromoText}
                invalidPromoCodeErrorText={resources.invalidPromoCodeErrorText}
                failedToAddPromoCodeErrorText={resources.failedToAddPromoCodeErrorText}
                failedToRemovePromoCodeErrorText={resources.failedToRemovePromoCodeErrorText}
                duplicatePromoCodeErrorText={resources.duplicatePromotionErrorText}
                context={this.props.context}
                id={this.props.id}
                typeName={this.props.typeName}
                data={{}}
                telemetryContent={this.telemetryContent}
                /* VSI Customization start */
                mfrmExpiredPromoCodeErrorText={resources.mfrmExpiredPromoCodeErrorText}
                mfrmDoesNotApplyToCartPromoCodeErrorText={resources.mfrmDoesNotApplyToCartPromoCodeErrorText}
                mfrmCannotBeCombinedPromoCodeErrorText={resources.mfrmCannotBeCombinedPromoCodeErrorText}
                mfrmUsageLimitPromoCodeErrorText={resources.mfrmUsageLimitPromoCodeErrorText}
                appliedPromoText={resources.appliedPromoText}
                notAppliedPromoText={resources.notAppliedPromoText}
                bonusItemsStateShare={this.props.data.mfiBonusItems.result}
            /* VSI Customization end */
            />
        );
    }

    private _generateAddToOrderTemplateDialogResources(resources: IMfrmCartResources): IAddToOrderTemplateDialogResources {
        return {
            addToOrderTemplateHeader: resources.addToOrderTemplateHeader,

            noOrderTemplatesMessage: resources.noOrderTemplatesMessage,
            noOrderTemplatesDescription: resources.noOrderTemplatesDescription,

            createAnOrderTemplateButtonText: resources.createAnOrderTemplateButtonText,
            createNewOrderTemplateButtonText: resources.createNewOrderTemplateButtonText,
            cancelOrderTemplateCreationButtonText: resources.cancelOrderTemplateCreationButtonText,

            selectTemplatesText: resources.selectTemplatesText,
            addToTemplateButtonText: resources.addToTemplateButtonText,
            lineItemsText: resources.lineItemsText
        };
    }

    private _generateItemAddedToOrderTemplateDialogResources(resources: IMfrmCartResources): IItemAddedToOrderTemplateDialogResources {
        return {
            viewOrderTemplateButtonText: resources.viewOrderTemplateButtonText,
            continueShoppingButtonText: resources.continueShoppingButtonText,
            itemAddedToOrderTemplateHeaderItemOneText: resources.itemAddedToOrderTemplateHeaderItemOneText,
            itemAddedToOrderTemplateHeaderItemFormatText: resources.itemAddedToOrderTemplateHeaderItemFormatText,
            itemAddedToOrderTemplateHeaderMessageText: resources.itemAddedToOrderTemplateHeaderMessageText,
            freePriceText: resources.freePriceText,
            originalPriceText: resources.originalPriceText,
            currentPriceText: resources.currentPriceText
        };
    }

    private _generateLinesAddedToOrderTemplateDialogResources(resources: IMfrmCartResources): ILinesAddedToOrderTemplateDialogResources {
        return {
            viewOrderTemplateButtonText: resources.viewOrderTemplateButtonText,
            continueShoppingButtonText: resources.continueShoppingButtonText,
            linesAddedToOrderTemplateHeaderItemsOneText: resources.linesAddedToOrderTemplateHeaderItemsOneText,
            linesAddedToOrderTemplateHeaderItemsFormatText: resources.linesAddedToOrderTemplateHeaderItemsFormatText,
            linesAddedToOrderTemplateHeaderLinesOneText: resources.linesAddedToOrderTemplateHeaderLinesOneText,
            linesAddedToOrderTemplateHeaderLinesFormatText: resources.linesAddedToOrderTemplateHeaderLinesFormatText,
            linesAddedToOrderTemplateHeaderMessageText: resources.linesAddedToOrderTemplateHeaderMessageText
        };
    }

    private _generateDuplicateItemsWhenAddingToOrderTemplateDialogResources(resources: IMfrmCartResources): IDuplicateItemsWhenAddingToOrderTemplateDialogResources {
        return {
            duplicatedProductsHeader: resources.duplicatedProductsHeader,
            duplicatedProductsDescription: resources.duplicatedProductsDescription,
            updateQuantityButtonText: resources.updateQuantityButtonText,
            cancelDuplicateItemsButtonText: resources.cancelDuplicateItemsButtonText
        };
    }

    private _generateOrderTemplateNameDialogResources(resources: IMfrmCartResources): IOrderTemplateNameDialogResources {
        return {
            orderTemplateHeaderLabel: resources.createOrderTemplateHeader,
            orderTemplateTitleLabel: resources.orderTemplateTitle,
            orderTemplateNameAriaLabel: resources.orderTemplateNameAriaLabel,
            orderTemplateDescription: resources.createOrderTemplateDescription,

            defaultOrderTemplateName: resources.defaultOrderTemplateName,

            orderTemplateButtonLabel: resources.createOrderTemplateButtonText,
            orderTemplateCancelButtonLabel: resources.cancelNewOrderTemplateCreationButtonText
        };
    }
    /* VSI customization START */
    // tslint:disable-next-line: max-func-body-length
    private _getCartLines(): ICartlinesViewProps[] | null {
        const {
            data: {
                cart: { result: cart },
                wishlists: { result: wishlists },
                orderTemplates: { result: orderTemplates },
                customerInformation: { result: customerInformation },
                productAttributes: { result: productAttributes }
            },
            context: {
                request: {
                    channel: {
                        PickupDeliveryModeCode
                    } = { PickupDeliveryModeCode: undefined }
                }
            },
            config: config,
            telemetry,
            resources,
            modules
        } = this.props;

        const imageSettings = this._getImageSettings(config.imageSettings);

        const cartlinesErrors: LineDataValidationFailure[] = this.state.cartlinesErrors;
        let storeSelectorId: string = '';
        if (modules && Object.keys(modules).length > 0 && modules.storeSelector && modules.storeSelector.length > 0) {
            storeSelectorId = modules.storeSelector[0].id;
        }
        return ArrayExtensions.hasElements(cart?.cart.CartLines) ? (
            CartLineItems({
                cartlines: cart!.cart.CartLines,
                storeSelectorModuleId: storeSelectorId,
                cartlinesErrors: cartlinesErrors,
                products: this.props.data.products.result,
                productSpecifications: this.state.productSpecifications,
                productCategoryPath: this.state.productCategoryPath,
                productAvailabilites: this.props.data.productAvailabilites.result,
                productAttributes,
                productDeliveryOptions: this.props.data.deliveryOptions.result,
                imageSettings: imageSettings,
                isStockCheckEnabled: this.props.context.app.config.enableStockCheck || false,
                maxCartlineQuantity: this.props.context.app.config.maxQuantityForCartLineItem,
                gridSettings: this.props.context.request.gridSettings!,
                context: this.props.context,
                typeName: this.props.typeName,
                id: this.props.id,
                pleaseContactExpertForOrder: this.props.config.pleaseContactExpertForOrder,
                sleepExpertContact: this.props.config.sleepExpertContact,
                sleepExpertChat: this.props.config.sleepExpertChat,
                resources: {
                    sizeString: resources.productDimensionTypeSize,
                    colorString: resources.productDimensionTypeColor,
                    configString: resources.productDimensionTypeConfiguration,
                    styleString: resources.productDimensionTypeStyle,
                    amountString: resources.productDimensionTypeAmount,
                    quantityDisplayString: resources.quantityDisplayText,
                    inputQuantityAriaLabel: resources.inputQuantityAriaLabel,
                    productPageLinkAriaLabel: resources.productPageLinkAriaLabel,
                    decrementButtonAriaLabel: resources.decrementButtonAriaLabel,
                    incrementButtonAriaLabel: resources.incrementButtonAriaLabel,
                    discountStringText: resources.discountStringText,
                    originalPriceText: resources.originalPriceText,
                    currentPriceText: resources.currentPriceText,
                    shippingChargesText: resources.shippingCharges,
                    outOfStockMsgText: resources.outOfStockMsgText,
                    removeFromCartText: resources.removeFromCartText,
                    similarAvailableProductText: resources.similarAvailableProductText,
                    removeFromCartTextWithoutBeforeCheckout: resources.removeFromCartTextWithoutBeforeCheckout,
                    homeDeliveryText: resources.homeDeliveryText,
                    // VSI Customization {52133}
                    itemsQuantityText: resources.itemsQuantityText,
                    productAriaText: resources.productAriaText,
                    variantAriaText: resources.variantAriaText,
                    promoAriaText: resources.promoAriaText,
                    priceAriaText: resources.priceAriaText,
                    discountPercentageAriaText: resources.discountPercentageAriaText,
                    editGiftCtaText: resources.editGiftCtaText,
                    dialogAriaText: resources.dialogAriaText,
                    removeButtonAriaText: resources.removeButtonAriaText,
                    appliedPromoText: resources.appliedPromoText,
                    notAppliedPromoText: resources.notAppliedPromoText
                    // VSI Customization {52133} End
                },
                telemetry: telemetry,
                addToWishlistButtonText: resources.addToWishlistButtonText,
                removeFromWishlistButtonText: resources.removeFromWishlistButtonText,
                removeButtonText: resources.removeCartButtonText,
                shipItText: resources.shipInsteadDisplayText,
                mfrmCtaGiftText: resources.mfrmGiftCtaText,
                pickitUpText: resources.pickItUpDisplayText,
                changeStoreText: resources.changeStoreDisplayText,
                storeSelectorStateManager: this.props.data.storeSelectorStateManager.result,
                outOfStockText: resources.outOfStockErrorMessage,
                outOfRangeOneText: resources.errorMessageOutOfRangeOneLeft,
                outOfRangeFormatText: resources.errorMessageOutOfRangeFormat,
                cartState: this.props.data.cart.result,
                orgUnitLocations: undefined,
                removeItemClickHandler: this._removeItemFromCartHandler,
                moveToWishlistSuccessHandler: this._moveToWishlistSuccessHandler,
                updateCartLinesQuantitySuccessHandler: this._updateCartlineQuantity,
                locationChangedHandler: this._fulfillmentStoreIdUpdated,
                wishlists: wishlists,
                defaultWishlistName: resources.defaultWishlistName,
                pickupDeliveryModeCode: PickupDeliveryModeCode,
                includeErrors: config.showOutOfStockErrors,
                showShippingChargesForLineItems: config.showShippingChargesForLineItems,
                telemetryContent: this.telemetryContent,
                isQuantityLimitsFeatureEnabled: this.isQuantityLimitsFeatureEnabled,

                addToOrderTemplateDialogResources: this._generateAddToOrderTemplateDialogResources(resources),
                createOrderTemplateDialogResources: this._generateOrderTemplateNameDialogResources(resources),
                itemAddedToOrderTemplateDialogResources: this._generateItemAddedToOrderTemplateDialogResources(resources),
                duplicateItemsWhenAddingToOrderTemplateDialogResources: this._generateDuplicateItemsWhenAddingToOrderTemplateDialogResources(resources),

                addToOrderTemplateButtonText: resources.addToOrderTemplateButtonText,
                addToOrderTemplateButtonTooltip: resources.addToOrderTemplateButtonTooltip,
                addToOrderTemplateMessage: resources.addToOrderTemplateMessage,
                addItemToOrderTemplateError: resources.addItemToOrderTemplateError,
                orderTemplates: orderTemplates,
                customerInformation: customerInformation,
                bonusItemsProductDetails: this.globalBonusItems,
                mfrmBonusModalHeading: resources.mfrmBonusModalHeading,
                mfrmBonusSubHeading: resources.mfrmBonusSubHeading,
                mfrmBonusAppliedCodeText: resources.mfrmBonusAppliedCodeText,
                mfrmAddItemToCartText: resources.mfrmAddItemToCartText,
                GiftCtaButton: this.ctaGiftButton,
                // ctaGiftHandler: this._showCtaGiftProducts,
                recievedBonusProduct: this._addCartlineFromModal,
                // sendState: this._sendSatateToCartLine,
                sendState: this.state.selectedBonusItems,
                setCartLineItems: this._setStateCartLine,
                // GiftCtaButton: this.ctaGiftButton, // this.state.ctaGiftButtonBoolean ?  this.ctaGiftButton : this.state.ctaGiftButtonBoolean,
                ctaGiftHandler: this._giftCart,
                isGiftCoupon: this.isGiftCoupon,
                deliveryByMessagesCore: this.state.deliveryByMessagesCore,
                deliveryByMessageSPDS: this.state.deliveryByMessageSPDS,
                hideDeliveryMessage: this.state.disableCheckoutButton,
                onEditGift: this._onEditGift
            })
        ) : null;
    }

    // 22192
    /* private _getCheckoutURL(isAuthenticated: boolean, guestCheckoutUrl: string, signInUrl: string): string {
        if (isAuthenticated) {
            return guestCheckoutUrl;
        }

        const absoluteUrlRegExp = new RegExp('^(?:[a-z]+:)?//', 'i');
        const isAbsoluteUrl = absoluteUrlRegExp.test(guestCheckoutUrl);
        let returnURL = guestCheckoutUrl;
        if (MsDyn365.isBrowser && !isAbsoluteUrl) {
            returnURL = `${window.location.origin}${guestCheckoutUrl}`;
        }

        return `${signInUrl}${signInUrl.indexOf('?') === -1 ? '?' : '&'}ru=${returnURL}`;
    }*/
    /* VSI customization END */

    private _getImageSettings = (imageSettings: IImageSettings | undefined): IImageSettings => {
        return imageSettings || {
            viewports: {
                xs: { q: `w=80&h=94&m=6`, w: 0, h: 0 },
                sm: { q: `w=148&h=174&m=6`, w: 0, h: 0 },
                lg: { q: `w=148&h=174&m=6`, w: 0, h: 0 }
            },
            lazyload: true
        };
    };
    private getProductCategory = async (productVariantIdArray: (number)[]): Promise<string | undefined> => {
        const {
            context: {
                request: {
                    apiSettings: {
                        catalogId
                    }
                }
            }
        } = this.props;
        const productsCategories = await getProductsCategoryPathsAction(new GetCategoryPathsInput(this.props.context.request, catalogId, productVariantIdArray), this.props.context.actionContext);

        const checkCategory = productsCategories.find(data => data.ProductId === productVariantIdArray[0]);
        const getSegments = checkCategory && checkCategory.CategoryPath && checkCategory.CategoryPath[0]?.Segments;
        let categoryName;
        if (getSegments) {
            categoryName = getSegments[getSegments?.length - 1]?.CategoryName!;
        }
        return categoryName;
    };

    private getProductBrand = async (productVariantIdArray: (number | undefined)[]): Promise<string | undefined> => {
        const {
            context: {
                actionContext: ctx,
                request: {
                    apiSettings: {
                        channelId
                    }
                }
            }
        } = this.props;
        const productSpecificationData: IAttributeValueProduct[] = await getAttributeValuesAsync({ callerContext: ctx, queryResultSettings: {} }, productVariantIdArray[0]!, channelId, 0);
        const newProductSpecificationData = productSpecificationData.filter(Boolean);
        let brandNameGet;
        if (newProductSpecificationData) {
            brandNameGet = newProductSpecificationData.find(item => item.Name === 'Brand')?.TextValue!;
        }
        return brandNameGet;
    };

    private _onEditGift = () => {
        let bonusItemArray: IProductBonusItems[] = [];
        /* VSI customization START */
        const getLocalStorage = getSetSelectedBonasItem('get');
        // const getLocalStorage = localStorage.getItem('selectedBonasItems');
        /* VSI customization END*/
        if (getLocalStorage) {
            bonusItemArray = getLocalStorage;
        }
        this.setState({
            selectedBonusItems: bonusItemArray
        });
    };

    private _tealiumCallonRemove = async (cartlineToRemove: CartLine, newQuantity: number): Promise<void> => {
        const {
            data: {
                products: { result }
            }
        } = this.props;
        const productVariantIdArray: (number)[] = [cartlineToRemove.ProductId!];
        const ProductDetail = result?.find(item => item.RecordId === cartlineToRemove.ProductId);
        const getCategory = await this.getProductCategory(productVariantIdArray);
        const getBrandName = await this.getProductBrand(productVariantIdArray);
        let exactQuantity = cartlineToRemove.Quantity;
        if (newQuantity) {
            exactQuantity = cartlineToRemove.Quantity! - newQuantity;
        }
        const productNameArray = [ProductDetail?.Name];
        const productPrice = [ProductDetail?.Price?.toString()];
        const itemIdArray = [cartlineToRemove.ItemId];
        const productQuantityArray = [exactQuantity?.toString()];
        const productCategoryArray = [getCategory];
        const productBrandNameArray = [getBrandName];

        await callUtagLink(
            {
                event: 'remove-from-cart',
                enhanced_action: 'remove',
                event_category: 'ecommerce',
                event_action: 'remove from cart',
                event_label: `DL - ${cartlineToRemove.ItemId} : DL - ${productNameArray[0]}`,
                event_noninteraction: 'false',
                product_id: itemIdArray,
                product_name: productNameArray,
                product_category: productCategoryArray,
                product_brand: productBrandNameArray,
                product_variant: productVariantIdArray,
                product_price: productPrice,
                product_quantity: productQuantityArray
            }
        );
    };
    private _selectedBonasItems = async (ProductId: number) => {
        /* VSI customization START */
        const getLocalStorage = getSetSelectedBonasItem('get');
        // const getLocalStorage = localStorage.getItem('selectedBonasItems');
        /* VSI customization END*/
        let bonusItemArray: IProductBonusItems[] = [];
        if (getLocalStorage) {
            bonusItemArray = getLocalStorage;
        }
        const newBonasState: IProductBonusItems[] = [];
        const getInitialSelectedItems = this.state.selectedBonusItems.length === 0 ? bonusItemArray : this.state.selectedBonusItems;
        getInitialSelectedItems.map((itemBonas) => {
            const newChildItems = itemBonas.children.filter(childData => Number(childData.BonusProductId) !== ProductId);
            itemBonas.children = newChildItems;
            newBonasState.push(itemBonas);
        });
        this.setState({
            selectedBonusItems: newBonasState,
            previousBonasModal: newBonasState
        });
        /* VSI customization START */
        getSetSelectedBonasItem('set', newBonasState);
        // localStorage.setItem('selectedBonasItems', JSON.stringify(newBonasState));
        /* VSI customization END*/
    };

    private getCartlineProductName = (cartlineToRemove: CartLine) => {
        const productDetails: IProductDetails[] = getSetProductData('get');
        if (hasArray(productDetails)) {
            for (const p of productDetails) {
                if (p.VariantRecordId === cartlineToRemove.ProductId) {
                    return p.brandAndProductName;
                }
            }
        }
        return '';
    };

    /* eslint-disable @typescript-eslint/no-floating-promises */
    private _removeItemFromCartHandler = async (cartlineToRemove: CartLine, url?: String) => {
        const {
            context: {
                actionContext,
                request: {
                    apiSettings: { channelId, catalogId }
                }
            },
            telemetry,
            data: { cart: result }
        } = this.props;
        const productName = this.getCartlineProductName(cartlineToRemove);
        this._tealiumCallonRemove(cartlineToRemove, 0);
        if (await result) {
            const input = {
                cartLineIds: [cartlineToRemove.LineId!.toString()]
            };
            const productSpecificationsCookieData = getSetProductData('get');
            /* VSI customization START */
            const psp = getSetProductSpec('get');
            // const psp = localStorage.getItem('product_specification');
            /* VSI customization END*/
            let pspData: IProductDetails[] = [];
            if (!psp) {
                pspData = [];
            } else {
                pspData = psp;
            }
            const saveProdForCatch = [...productSpecificationsCookieData];
            const saveProdForLocalStorageInCatch = [...pspData];
            if (productSpecificationsCookieData) {
                productSpecificationsCookieData.map((productSpec: IProductDetails, index: number) => {
                    if (productSpec && productSpec.VariantRecordId === cartlineToRemove.ProductId) {
                        productSpecificationsCookieData.splice(index, 1);
                        pspData.splice(index, 1);
                    }
                });
                getSetProductData('set', productSpecificationsCookieData);
                /* VSI customization START */
                getSetProductSpec('set', pspData);
                // localStorage.setItem('product_specification', JSON.stringify(pspData));
                /* VSI customization END*/
            }
            this.props.context.actionContext.update(new PromoCodeAppliedOnCartInput(), { isInProcess: true });
            (await result).removeCartLines(input)
                .then(async () => {
                    /* VSI customization START - Tracking 79374 */
                    this.tealiumCallOnRemoveForGA(this.props.data.products, cartlineToRemove?.ItemId);
                    /* VSI customization END - Tracking 79374 */
                    this.props.context.actionContext.update(new PromoCodeAppliedOnCartInput(), { isInProcess: false });
                    this._validateCart()
                        .catch((error) => {
                            telemetry.warning(error);
                            telemetry.debug('Unable to validate Cart when Remove items');
                        });
                    this.setState({ showRemoveNotification: true, showProductTitle: productName });
                    this.timer = setTimeout(() => {
                        this.setState({ showRemoveNotification: false, showProductTitle: '' });
                    }, 8000);
                    await this._selectedBonasItems(cartlineToRemove.ProductId!);
                    const updatedProductSpecificationsCookieData = getSetProductData('get');
                    const CheckforSpecialOffer = updatedProductSpecificationsCookieData.find((item: IProductDetails) => item.brandName === 'Beautyrest' && (item.collection === 'Beyond' || item.collection === 'Beyond Hybrid'));
                    if (CheckforSpecialOffer) {
                        this.props.context.actionContext.update(new TriggerSpecialSynchronyFinancingInput(), { triggerSpecialFinancing: true });
                    } else {
                        this.props.context.actionContext.update(new TriggerSpecialSynchronyFinancingInput(), { triggerSpecialFinancing: false });
                    }
                    this.setState({
                        cartRender: false
                    });
                    getAttributesForCartLineItems(new AttributesForSelectedVariantInput(channelId, catalogId), this.props.context.actionContext).then((res) => {
                        actionContext.update(new AttributesForSelectedVariantInput(channelId, catalogId), res);
                        this.returnCoreProductCheck();
                        this.setState({ productAttributes: res });
                    }).catch((error) => {
                        telemetry.warning(error);
                        telemetry.debug('Unable to validate Attributes CartLine Items when Remove items');
                    });
                    this.setState({ productDetails: productSpecificationsCookieData });
                    this._getUpdateLinbeDeliveryCall(this.props, this.state.newZipCode, this.state.errorZipCode, '');
                    if (url) {
                        window.location.href = `${url}`;
                    }
                    this.refreshCartCookie();
                    this.tealiumCartDetails(productSpecificationsCookieData, pspData);
                    const cookies = new Cookies();
                    const zipCode = cookies.get(`zip_code`);
                    if(zipCode){
                        this._getStateFromZipCode(this.props, zipCode, true);
                    }
                })
                .catch((error) => {
                    getSetProductData('set', saveProdForCatch);
                    /* VSI customization START */
                    getSetProductSpec('set', saveProdForLocalStorageInCatch);
                    // localStorage.setItem('product_specification', JSON.stringify(saveProdForLocalStorageInCatch));
                    /* VSI customization END*/
                    this.props.telemetry.warning(error);
                    this.props.telemetry.debug('Unable to Remove Cart Line when remove items');
                });
        }
    };

    private _onlyRemoveItemFromCartHandler = async (cartlineToRemove: CartLine) => {
        const {
            data: { cart: result },
            telemetry
        } = this.props;
        if (await result) {
            const productSpecificationsCookieData = getSetProductData('get');
            if (productSpecificationsCookieData) {
                const newProductSpecificationsCookieData = productSpecificationsCookieData.filter((item: { VariantRecordId: number }) => item.VariantRecordId !== cartlineToRemove.ProductId);
                getSetProductData('set', newProductSpecificationsCookieData);
            }
            const input = {
                cartLineIds: [cartlineToRemove.LineId!.toString()]
            };
            (await result).removeCartLines(input)
                .then(() => {
                    this._validateCart()
                        .catch((error) => {
                            telemetry.warning(error);
                            telemetry.debug('Unable to validate Cart when remove service item');
                        });
                    this.refreshCartCookie();
                })
                .catch((error) => {
                    telemetry.warning(error);
                    telemetry.debug('Unable to Remove Cart Line when reomve service item');
                });
        }
    };

    private _tealiumCallonAddQuantity = async (cartlineToRemove: CartLine, newQuantity: number): Promise<void> => {
        const {
            data: {
                products: { result }
            }
        } = this.props;
        const productVariantIdArray: (number)[] = [cartlineToRemove.ProductId!];
        const ProductDetail = result?.find(item => item.RecordId === cartlineToRemove.ProductId);
        const getCategory = await this.getProductCategory(productVariantIdArray);
        const getBrandName = await this.getProductBrand(productVariantIdArray);
        let exactQuantity = cartlineToRemove.Quantity;
        if (newQuantity) {
            exactQuantity = newQuantity - cartlineToRemove.Quantity!;
        }
        const productNameArray = [ProductDetail?.Name];
        const productPrice = [ProductDetail?.Price?.toString()];
        const itemIdArray = [cartlineToRemove.ItemId];
        const productQuantityArray = [exactQuantity?.toString()];
        const productCategoryArray = [getCategory];
        const productBrandNameArray = [getBrandName];

        await callUtagLink(
            {
                event: 'add-from-cart',
                enhanced_action: 'add',
                event_category: 'ecommerce',
                event_action: 'add from cart',
                event_label: `DL - ${cartlineToRemove.ItemId} : DL - ${productNameArray[0]}`,
                event_noninteraction: 'false',
                product_id: itemIdArray,
                product_name: productNameArray,
                product_category: productCategoryArray,
                product_brand: productBrandNameArray,
                product_variant: productVariantIdArray,
                product_price: productPrice,
                product_quantity: productQuantityArray
            }
        );
    };

    /* eslint-disable @typescript-eslint/no-floating-promises */
    private _moveToWishlistSuccessHandler = (result: IWishlistActionSuccessResult, cartline?: CartLine) => {
        if (result.status === 'ADDED' && cartline) {
            this._removeItemFromCartHandler(cartline);
        }
    };

    // tslint:disable: no-floating-promises
    private async _updateDeliveryMessageForUpdatedQuantityOfCoreProduct(cartlineToUpdate: CartLine, quantity: number): Promise<void> {
        let coreDelMsgUpdated = false;
        const productSpecificationsCookieData = getSetProductData('get');
        const updatedCoreDelMsgItems: ICoreDeliveryByMessage[] = [];
        for (const coreProdMsg of this.state.deliveryByMessagesCore) {
            updatedCoreDelMsgItems.push(coreProdMsg);
            for (const productSpecItem of productSpecificationsCookieData) {
                if (productSpecItem.VariantRecordId === cartlineToUpdate.ProductId && productSpecItem.typeOfProduct === 'Core' && coreProdMsg.variantID === cartlineToUpdate.ProductId?.toString()) {
                    const delMsgObj = await _coreProductATPCall(this.props, cartlineToUpdate.ProductId, cartlineToUpdate.ItemId, quantity);
                    const delMsgObjSplitted = delMsgObj.split(',');
                    updatedCoreDelMsgItems[updatedCoreDelMsgItems.length - 1].deliveryByMessage = delMsgObjSplitted[2];
                    coreDelMsgUpdated = true;
                }
            }
        }
        if (coreDelMsgUpdated) {
            this.setState({ deliveryByMessagesCore: updatedCoreDelMsgItems });
        }
    }
    private _updateCartLineUpdateQuantity = async (cartlineToUpdate: CartLine, quantity: number) => {
        const { ProductId } = cartlineToUpdate;
        const newSepecifications = this.state.productDetails.map((item) => {
            if (item.VariantRecordId === ProductId) {
                item.Quantity = quantity;
            }
            return item;
        });
        this.setState({ productSpecifications: newSepecifications, productDetails: newSepecifications });
        getSetProductData('set', newSepecifications);
    };

    /* eslint-disable @typescript-eslint/no-floating-promises */
    private _updateCartlineQuantityWithAvailableQuantity = async (cartlineToUpdate: CartLine, quantity: number): Promise<boolean> => {
        const { ProductId, Quantity } = cartlineToUpdate;
        let fixedQuantity = quantity;
        const checkProduct = this.state.productSpecifications.find((item) => item.VariantRecordId === ProductId);
        const typeProduct = checkProduct?.typeOfProduct;
        if (Quantity! > quantity) {
            this._tealiumCallonRemove(cartlineToUpdate, quantity);
        } else {
            this._tealiumCallonAddQuantity(cartlineToUpdate, quantity);
        }
        fixedQuantity = quantity;
        await this._updateCartLineUpdateQuantity(cartlineToUpdate, fixedQuantity);
        this.setState({
            cartRender: true
        });
        this._updateDeliveryMessageForUpdatedQuantityOfCoreProduct(cartlineToUpdate, fixedQuantity);
        if (this.props.data.cart.result) {
            // const getLocalStorage = localStorage.getItem('BonusItemProduct');
            // const cartId = this.props.data.cart.result.cart.Id;
            // const context = this.props.context;
            const parentProduct = this._checkCartLineId(cartlineToUpdate);
            // const cartLinesInCart = this.props.data.cart.result?.cart.CartLines;
            // const bonusItemArray: IMFIBonusItemsResponse[] = [];
            // const bonusItemsCartLine: CartLine[] = [];
            if (parentProduct) {
                //     bonusItemArray = getLocalStorage && JSON.parse(getLocalStorage);
                //     // get bonus item array from localstorage
                //     bonusItemArray.map((bonusItemsArray) => {
                //         // bonus product array against applied promocode
                //         bonusItemsArray.BonusItemsDetails?.map((bonusItemDetail) => {
                //             // bonus item details array with items of parent product
                //             bonusItemDetail.Items?.map((item) => {
                //                 // check if cartlineId is equal to parent or qualifying Id
                //                 if (item.QualifyingItemId === cartlineToUpdate.ItemId) {
                //                     bonusItemsCartLine.push(cartlineToUpdate);
                //                     item.BonusItems?.map((bonusItems) => {
                //                         cartLinesInCart?.filter((lines) => {
                //                             if (bonusItems.BonusItemId === lines.ItemId) {
                //                                 bonusItemsCartLine.push(lines);
                //                             }
                //                         });
                //                     });
                //                 }
                //             });
                //         });
                //     });
                //     bonusItemsCartLine.filter((bonusLine) => {
                //         bonusLine.Quantity = fixedQuantity;
                //     });
                //     const updateCart = updateCartLinesAsync({ callerContext: context.actionContext }, cartId, bonusItemsCartLine);
                //     updateCart.then(async (result) => {
                //         this.setState({
                //             cartRender: false
                //         });
                //         const cartState = await getCartState(context.actionContext);
                //         await cartState.refreshCart({});
                //     }).catch((e) => {
                //         console.log(e, 'Error');
                //     });
            } else {
                const input = {
                    cartLineId: cartlineToUpdate.LineId!.toString(),
                    newQuantity: fixedQuantity,
                    additionalProperties: { orderQuantityLimitsFeatureIsEnabled: this.isQuantityLimitsFeatureEnabled }
                };
                this.props.context.actionContext.update(new PromoCodeAppliedOnCartInput(), { isInProcess: true });
                this.props.data.cart.result.updateCartLineQuantity(input)
                    .then(async (result: ICartActionResult) => {
                        this.setState({
                            cartRender: false
                        });
                        this.props.context.actionContext.update(new PromoCodeAppliedOnCartInput(), { isInProcess: false });
                        this.refreshCartCookie();
                        const newSepecifications = this.state.productSpecifications.map((item) => {
                            if (item.VariantRecordId === ProductId) {
                                item.quantityMessage = true;
                            }
                            return item;
                        });
                        this.setState({ productSpecifications: newSepecifications, productDetails: newSepecifications });
                        getSetProductData('set', newSepecifications);
                        this.timer = setTimeout(() => {
                            const newSpecifications = this.state.productSpecifications.map((item) => {
                                if (item.VariantRecordId === ProductId) {
                                    item.quantityMessage = false;
                                }
                                return item;
                            });
                            this.setState({ productSpecifications: newSpecifications, productDetails: newSpecifications });
                            getSetProductData('set', newSpecifications);
                        }, 5000);
                        if (result.status === 'SUCCESS') {
                            this._getUpdateLinbeDeliveryCall(this.props, this.state.newZipCode, this.state.errorZipCode, typeProduct!);
                            this._validateCart().catch((error) => {
                                this.props.telemetry.warning(error);
                                this.props.telemetry.debug('Unable to validate Cart when update quantity');
                            });
                            return true;
                        }
                        return false;
                    })
                    .catch((error) => {
                        this.props.telemetry.warning(error);
                        this.props.telemetry.debug('Unable to update Cart Line quantity when change quantity');
                        this.props.context.actionContext.update(new PromoCodeAppliedOnCartInput(), { isInProcess: false });
                    });
            }

            return false;
        }
        return false;
    };

    private hasSameDiscountCodes = (qualifyingItem: CartLine, bonusItem: CartLine) => {
        const qualifyingDiscountLines = qualifyingItem.DiscountLines;
        const bonusItemDiscountLines = bonusItem.DiscountLines;
        if (hasArray(qualifyingDiscountLines) && hasArray(bonusItemDiscountLines)) {
            for (const qdl of qualifyingDiscountLines!) {
                for (const bdl of bonusItemDiscountLines!) {
                    if ((qdl.DiscountCode === bdl.DiscountCode) && qdl.DiscountCode && this._isBonusCoupons(qdl.DiscountCode)) {
                        return true;
                    }
                }
            }
        }
        return false;
    };

    private _isBonusCoupons = (appliedPromoCoupons: string): boolean => {
        let checkBonusCoupons: boolean | undefined = false;
        /* VSI customization START */
        const getLocalstorageBonusCoupons = getSetBonusCouponDetail('get');
        // const getLocalstorageBonusCoupons = localStorage.getItem('bonusCouponsDetail');
        /* VSI customization END*/
        if (getLocalstorageBonusCoupons) {
            const bonusCouponsArray: IMFIGetBonusCouponsDetailsResponse[] = getLocalstorageBonusCoupons;
            bonusCouponsArray?.map(coupons => {
                checkBonusCoupons = coupons.BonusCoupons?.some(item => item.BonusCoupon?.toUpperCase() === appliedPromoCoupons.toUpperCase() || (item.BonusCoupon && (isOneOfChildBonusCoupons(appliedPromoCoupons, item.BonusCoupon))));
                return checkBonusCoupons;
            });
        }
        return checkBonusCoupons;
    };

    /* eslint-disable @typescript-eslint/no-floating-promises */
    private _updateCartlineQuantity = async (cartlineToUpdate: CartLine, quantity: number): Promise<boolean> => {
        const { ProductId, Quantity } = cartlineToUpdate;
        let fixedQuantity = quantity;
        const checkProduct = this.state.productSpecifications.find((item) => item.VariantRecordId === ProductId);
        const typeProduct = checkProduct?.typeOfProduct;
        let avialbleQuantity = checkProduct?.availableQuantity;
        if (typeProduct?.toLowerCase() === 'parcel') {
            avialbleQuantity = await this.checkUniqueSmallParcelProduct(checkProduct?.VariantRecordId!, checkProduct?.ItemId!);
        }
        if (Quantity! > quantity) {
            this._tealiumCallonRemove(cartlineToUpdate, quantity);
        } else {
            this._tealiumCallonAddQuantity(cartlineToUpdate, quantity);
        }
        if (avialbleQuantity) {
            if (quantity > avialbleQuantity) {
                fixedQuantity = avialbleQuantity;
            }
        }
        await this._updateCartLineUpdateQuantity(cartlineToUpdate, fixedQuantity);
        this.setState({
            cartRender: true
        });
        this._updateDeliveryMessageForUpdatedQuantityOfCoreProduct(cartlineToUpdate, fixedQuantity);
        if (this.props.data.cart.result) {
            /* VSI customization START */
            const getLocalStorage = getSetBonusItemProd('get');
            // const getLocalStorage = localStorage.getItem('BonusItemProduct');
            /* VSI customization END*/
            const cartId = this.props.data.cart.result.cart.Id;
            const context = this.props.context;
            const parentProduct = this._checkCartLineId(cartlineToUpdate);
            const cartLinesInCart = this.props.data.cart.result?.cart.CartLines;
            let bonusItemArray: IMFIBonusItemsResponse[] = [];
            const bonusItemsCartLine: CartLine[] = [];
            if (parentProduct) {
                if (!hasArray(getLocalStorage)) {
                    if (getLocalStorage && hasArray(getLocalStorage.BonusItemsDetails)) {
                        bonusItemArray = [getLocalStorage];
                    }
                } else {
                    bonusItemArray = getLocalStorage;
                }
                // get bonus item array from localstorage
                bonusItemArray.map((bonusItemsArray) => {
                    // bonus product array against applied promocode
                    bonusItemsArray.BonusItemsDetails?.map((bonusItemDetail) => {
                        // bonus item details array with items of parent product
                        bonusItemDetail.Items?.map((item) => {
                            // check if cartlineId is equal to parent or qualifying Id
                            if (item.QualifyingItemId === cartlineToUpdate.ItemId && item.QualifyingProductId?.toString() === cartlineToUpdate.ProductId?.toString()) {
                                bonusItemsCartLine.push(cartlineToUpdate);
                                item.BonusItems?.map((bonusItems) => {
                                    cartLinesInCart?.filter((lines) => {
                                        if ((bonusItems.BonusItemId === lines.ItemId) && this.hasSameDiscountCodes(cartlineToUpdate, lines)) {
                                            bonusItemsCartLine.push(lines);
                                        }
                                    });
                                });
                            }
                        });
                    });
                });
                bonusItemsCartLine.filter((bonusLine) => {
                    bonusLine.Quantity = fixedQuantity;
                });
                const updateCart = updateCartLinesAsync({ callerContext: context.actionContext }, cartId, bonusItemsCartLine);
                this.props.context.actionContext.update(new PromoCodeAppliedOnCartInput(), { isInProcess: true });
                updateCart.then(async (result) => {
                    this.props.context.actionContext.update(new PromoCodeAppliedOnCartInput(), { isInProcess: false });
                    this.setState({
                        cartRender: false
                    });
                    const cartState = await getCartState(context.actionContext);
                    await cartState.refreshCart({});
                    this._getUpdateLinbeDeliveryCall(this.props, this.state.newZipCode, this.state.errorZipCode, typeProduct!);
                    this._validateCart().catch((error) => {
                        this.props.telemetry.warning(error);
                        this.props.telemetry.debug('Unable to validate Cart when update quantiy with available');
                    });
                }).catch((error) => {
                    this.props.telemetry.warning(error);
                    this.props.telemetry.debug('Unable to updateCartLinesAsync with available quantity');
                    this.props.context.actionContext.update(new PromoCodeAppliedOnCartInput(), { isInProcess: false });
                });
            } else {
                const input = {
                    cartLineId: cartlineToUpdate.LineId!.toString(),
                    newQuantity: fixedQuantity,
                    additionalProperties: { orderQuantityLimitsFeatureIsEnabled: this.isQuantityLimitsFeatureEnabled }
                };
                this.props.context.actionContext.update(new PromoCodeAppliedOnCartInput(), { isInProcess: true });
                this.props.data.cart.result.updateCartLineQuantity(input)
                    .then(async (result: ICartActionResult) => {
                        this.props.context.actionContext.update(new PromoCodeAppliedOnCartInput(), { isInProcess: false });
                        this.setState({
                            cartRender: false
                        });
                        this.refreshCartCookie();
                        if (avialbleQuantity) {
                            if (quantity > avialbleQuantity) {
                                const newSepecifications = this.state.productSpecifications.map((item) => {
                                    if (item.VariantRecordId === ProductId) {
                                        item.quantityMessage = true;
                                    }
                                    return item;
                                });
                                this.setState({ productSpecifications: newSepecifications, productDetails: newSepecifications });
                                getSetProductData('set', newSepecifications);
                            } else {
                                const newSepecifications = this.state.productSpecifications.map((item) => {
                                    if (item.VariantRecordId === ProductId) {
                                        item.quantityMessage = false;
                                    }
                                    return item;
                                });
                                this.setState({ productSpecifications: newSepecifications, productDetails: newSepecifications });
                                getSetProductData('set', newSepecifications);
                            }
                            if (quantity > avialbleQuantity) {
                                this.timer = setTimeout(() => {
                                    const newSepecifications = this.state.productSpecifications.map((item) => {
                                        if (item.VariantRecordId === ProductId) {
                                            item.quantityMessage = false;
                                        }
                                        return item;
                                    });
                                    this.setState({ productSpecifications: newSepecifications, productDetails: newSepecifications });
                                    getSetProductData('set', newSepecifications);
                                }, 5000);
                            }
                        }
                        if (result.status === 'SUCCESS') {
                            this._getUpdateLinbeDeliveryCall(this.props, this.state.newZipCode, this.state.errorZipCode, typeProduct!);
                            this._validateCart().catch((error) => {
                                this.props.telemetry.warning(error);
                                this.props.telemetry.debug('Unable to validate Cart when success full update quantiy');
                            });
                            return true;
                        }
                        return false;
                    })
                    .catch((error) => {
                        this.props.telemetry.warning(error);
                        this.props.telemetry.debug('Unable to update Cart Line quantity when success full update quantiy');
                    });
            }

            return false;
        }
        return false;
    };

    // tslint:disable-next-line: function-name
    private async checkUniqueSmallParcelProduct(VariantRecordId: number, ItemId: string): Promise<number> {
        const cookies = new Cookies();
        const zipcode = cookies.get('zip_code');
        const date = new Date();
        const productsSpecChange = this.state.productSpecifications;
        let quantity: number = 0;
        const currentDate = date.setDate(date.getDate());
        const tempProducts: { ItemId: string; Quantity: number; VariantRecordId: string }[] = [];
        tempProducts.push({
            ItemId: ItemId,
            Quantity: 1,
            VariantRecordId: VariantRecordId?.toString()
        });
        if (tempProducts?.length) {
            const requestBody = {
                InventoryType: 'Delivery',
                StoreId: '',
                Weeks: this.props.context.app && this.props.context.app.config.weeksPDPforATPCall,
                RequestedDate: dayjs(currentDate).format('MM/DD/YYYY'),
                ZipCode: zipcode, // '77047',
                ItemLines: tempProducts,
                NoOfPriorityDC: this.props.context.app.config.NoOfPriorityDC ? this.props.context.app.config.NoOfPriorityDC : 0
            };
            const result = await _getInventoryQuantity(requestBody, this.props.context, false);
            const quantityAvailable: string | null | undefined = result && result.ATPInventoryStatusData && result.ATPInventoryStatusData[0]?.Quantity;
            if (result && result.ATPInventoryStatusData && result.ATPInventoryStatusData[0]?.Available !== 'NO') {
                productsSpecChange.forEach((prod) => {
                    if (prod.VariantRecordId === VariantRecordId) {
                        prod.outOfStock = false;
                    }
                });
                this.setState({
                    productSpecifications: productsSpecChange,
                    productDetails: productsSpecChange
                });
                getSetProductData('set', productsSpecChange);
            }
            if (quantityAvailable) {
                quantity = +quantityAvailable;
            }
        }
        return quantity;
    }

    // check cartline id is parent ID or not
    private _checkCartLineId = (cartlineToUpdate: CartLine): boolean => {
        /* VSI customization START */
        const getLocalStorage = getSetBonusItemProd('get');
        // const getLocalStorage = localStorage.getItem('BonusItemProduct');
        /* VSI customization END*/
        let bonusItemArray: IMFIBonusItemsResponse[] = [];
        let parenProduct: boolean = false;
        if (getLocalStorage) {
            if (!hasArray(getLocalStorage)) {
                if (getLocalStorage && hasArray(getLocalStorage.BonusItemsDetails)) {
                    bonusItemArray = [getLocalStorage];
                }
            } else {
                bonusItemArray = getLocalStorage;
            }
            // get bonus item array from localstorage
            bonusItemArray.map((bonusItemsArray) => {
                // bonus product array against applied promocode
                bonusItemsArray.BonusItemsDetails?.map((bonusItemDetail) => {
                    // bonus item details array with items of parent product
                    bonusItemDetail.Items?.map((item) => {
                        // check if cartlineId is equal to parent or qualifying Id
                        if (item.QualifyingItemId === cartlineToUpdate.ItemId && item.QualifyingProductId?.toString() === cartlineToUpdate.ProductId?.toString()) {
                            return parenProduct = true;
                        }
                        return;
                    });
                });
            });
        }
        return parenProduct ? true : false;
    };

    // private _sendSatateToCartLine = () => {
    //     return this.state.selectedBonusItems;
    // }

    private _setStateCartLine = (bonusItemsFromModal: IProductBonusItems) => {
        const elementsIndex = this.state.selectedBonusItems.findIndex(element => element?.parentId === bonusItemsFromModal?.parentId);
        const newArray = this.state.selectedBonusItems;
        if (elementsIndex > -1) {
            newArray[elementsIndex] = bonusItemsFromModal;
        } else {
            newArray.push(bonusItemsFromModal);
        }
        this.setState({
            selectedBonusItems: newArray
        });
    };

    // private _getParentID = async (productId: number): Promise<number> => {
    //     let productParentId: number = 0;
    //     const getLocalStorage = localStorage.getItem('BonusItemProduct');
    //     let bonusItemArray: IMFIBonusItemsResponse[] = [];
    //     if (getLocalStorage) {
    //         bonusItemArray = JSON.parse(getLocalStorage);
    //     }
    //     bonusItemArray?.map((bonusitemdetail) => {
    //         bonusitemdetail?.BonusItemsDetails?.map((bonusitem) => {
    //             bonusitem.Items?.map((item) => {
    //                 if(Number(item.QualifyingProductId) === productId) {
    //                     productParentId = Number(item.QualifyingProductId);
    //                 } else {
    //                     item?.BonusItems!.map((bonasData) => {
    //                         if(Number(bonasData.BonusProductId) === productId) {
    //                             productParentId = Number(item.QualifyingProductId);
    //                         }
    //                     });
    //                 }
    //             });
    //         });
    //     });
    //     return productParentId;
    // }

    private _addCartlineFromModal = async (productId: number | undefined, bonusItemsFromModal: IMFIBonusItemDetails[], productQuantityCount: number) => {
        const elementsIndex = this.state.selectedBonusItems.findIndex(element => element?.parentId === productId);
        const {
            context: {
                request: {
                    apiSettings: {
                        channelId,
                        catalogId
                    }
                }
            }
        } = this.props;
        /* VSI customization START */
        const getLocalStorage = getSetPrevBonasModalState('get');
        // const getLocalStorage = localStorage.getItem('previousBonasModalState');
        /* VSI customization END*/
        let bonusItemArray: IProductBonusItems[] = [];
        if (getLocalStorage) {
            bonusItemArray = getLocalStorage;
        }
        const getParentId = productId;
        const getStateBonasItems = this.state.previousBonasModal.length === 0 ? bonusItemArray : this.state.previousBonasModal;
        const newPreviousData: IProductBonusItems[] = [];
        const bonasPrevoiusArray = [];
        const bonasPreviousData = { parentId: getParentId, children: bonusItemsFromModal };
        bonasPrevoiusArray.push(bonasPreviousData);
        let removeBonusArray: IMFIBonusItemDetails[] = [];
        if (getStateBonasItems.length > 0) {
            getStateBonasItems.map((item) => {
                if (item.parentId !== getParentId) {
                    const checkIfExist = !!getStateBonasItems.find(itemFilter => itemFilter.parentId === getParentId);
                    if (!checkIfExist) {
                        newPreviousData.push(bonasPreviousData);
                    }
                    const checkUpdate = newPreviousData.includes(item);
                    if (!checkUpdate) {
                        newPreviousData.push(item);
                    }
                } else {
                    let result: IMFIBonusItemDetails[] = [];
                    const childDataArray = item.children;
                    result = childDataArray.filter(data => bonusItemsFromModal.every(bonasItem => data.BonusProductId !== bonasItem.BonusProductId));
                    removeBonusArray = result;
                    item.children = bonusItemsFromModal;
                    if (bonusItemsFromModal.length > 0) {
                        newPreviousData.push(item);
                    }
                }
            });

            // this.setState({
            //     previousBonasModal: newPreviousData
            // });
        } else {
            newPreviousData.push(bonasPreviousData);
        }
        const productArrayData = [];
        const procductData = {};
        procductData[productId!] = bonusItemsFromModal;
        productArrayData.push(procductData);
        const { data: { cart: { result: cart } } } = this.props;
        const cartId = cart?.cart.Id;
        // const cartVersion = (await this.props.data.cart).cart.Version;
        const newArray = this.state.selectedBonusItems;
        if (elementsIndex > -1) {
            newArray[elementsIndex] = { parentId: productId, children: bonusItemsFromModal };
        } else {
            newArray.push({ parentId: productId, children: bonusItemsFromModal });
        }
        this.setState({
            selectedBonusItems: newArray
        });
        /* VSI customization START */
        getSetSelectedBonasItem('set', newArray);
        // localStorage.setItem('selectedBonasItems', JSON.stringify(newArray));
        /* VSI customization END*/
        const deletedCartLines: string[] = [];

        // let onlyInA = this.props.data.cart.result?.cart.CartLines?.filter(this.comparer(bonusItemsFromModal))
        // const getLocalStorage = localStorage.getItem('BonusItemProduct');
        const bonusCartlines: CartLine[] | null = [];
        // let parentItemId: string | undefined = '';
        // let bonusItemArray: IMFIBonusItemsResponse[] = [];
        // if (getLocalStorage) {
        //     bonusItemArray = JSON.parse(getLocalStorage);
        // }
        // bonusItemArray?.map((bonusitemdetail) => {
        //     bonusitemdetail?.BonusItemsDetails?.map((bonusitem) => {
        //         bonusitem.Items?.map((item) => {
        //             parentItemId = item?.QualifyingItemId;
        //         });
        //     });
        // });
        /* VSI customization START */
        getSetPrevBonasModalState('set', newPreviousData);
        // localStorage.setItem('previousBonasModalState', JSON.stringify(newPreviousData));
        /* VSI customization END*/
        this.setState({
            previousBonasModal: newPreviousData
        });
        this.props.data.cart.result?.cart.CartLines?.map((cartline) => {
            const checkCartLine = !!removeBonusArray.find(remove => remove.BonusItemId === cartline.ItemId);
            if (checkCartLine) {
                deletedCartLines.push(cartline.LineId!);
            }
        });
        if (this.props.data.cart.result) {
            if (deletedCartLines.length > 0) {
                // deletedCartLines.map(async(item) => {
                //     await this._removeItemFromCartHandler(item);
                // });
                const input = {
                    cartLineIds: deletedCartLines
                };
                await this.props.data.cart.result.removeCartLines(input)
                    .then(() => {
                        this._validateCart()
                            .catch((error) => {
                                this.props.telemetry.warning(error);
                                this.props.telemetry.debug('Unable to validate Cart when deleted cart line from modal');
                            });
                        this.refreshCartCookie();
                    })
                    .catch((error) => {
                        this.props.telemetry.warning(error);
                        this.props.telemetry.debug('Unable to Remove Cart Line when add cart line from modal');
                    });
            }
        }
        if (bonusItemsFromModal.length > 0) {
            const cartState = await getCartState(this.props.context.actionContext);
            let updatedParentQuantity = 0;
            hasArray(this.props.data.cart.result?.cart?.CartLines)?.filter((cartline) => {
                if (cartline.ProductId === productId) {
                    updatedParentQuantity = cartline.Quantity ? cartline.Quantity : 0;
                }
            });
            bonusItemsFromModal?.map((product) => {
                let checkItemAlredyInCart: boolean = false;
                hasArray(this.props.data.cart.result?.cart?.CartLines)?.filter((cartline) => {
                    if (product.BonusItemId === cartline.ItemId) {
                        checkItemAlredyInCart = true;
                    }
                });
                if (!checkItemAlredyInCart && product.AdditionToCart === 'Manual') {
                    const productToAdd = {
                        ProductId: Number(product.BonusProductId),
                        // ProductTypeValue: 5,
                        EntryMethodTypeValue: 5,
                        ItemId: product.BonusItemId,
                        Quantity: updatedParentQuantity
                    };
                    bonusCartlines.push(productToAdd);
                }
            });
            const cookies = new Cookies();
            const zipcode = cookies.get('zip_code');
            // const cartID = (await this.props.data.cart).cart.Id;
            if (bonusCartlines.length > 0) {
                this._validateCart().then(async () => {
                    this.props.context.actionContext.update(new PromoCodeAppliedOnCartInput(), { isInProcess: true });
                    await addCartLinesAsync({ callerContext: this.props.context.actionContext }, cartId!, bonusCartlines).then(async newCart => {
                        await cartState.refreshCart({});
                        this.props.context.actionContext.update(new PromoCodeAppliedOnCartInput(), { isInProcess: false });
                        reshuffleItems(this.props.data.cart.result?.cart.CartLines ? this.props.data.cart.result?.cart.CartLines : []);
                        this.refreshCartCookie();
                        this._getStateFromZipCode(this.props, zipcode, true);
                        getAttributesForCartLineItems(new AttributesForSelectedVariantInput(channelId, catalogId), this.props.context.actionContext).then((res) => {
                            this.props.context.actionContext.update(new AttributesForSelectedVariantInput(channelId, catalogId), res);
                            this.returnCoreProductCheck();
                        }).catch((error) => {
                            this.props.telemetry.warning(error);
                            this.props.telemetry.debug('Unable to validate Attributes CartLine Items when add cart line from modal');
                        });
                    });
                }).catch((error) => {
                    this.props.telemetry.warning(error);
                    this.props.telemetry.debug('Unable to validate Cart when add cart line from modal');
                });
            }
        }
    };

    private _fulfillmentStoreIdUpdated = () => {
        this._validateCart().catch((error) => {
            this.props.telemetry.warning(error);
            this.props.telemetry.debug('Unable to validate Cart _fulfillmentStoreIdUpdated');
        });
    };

    private async _validateCart(retry: boolean = true): Promise<void> {
        if (!this.isQuantityLimitsFeatureEnabled) {
            return AsyncResult.resolve();
        }

        const {
            data: { cart: cart },
            telemetry
        } = this.props;
        let cartResult: ICartState;
        try {
            cartResult = await cart;
        } catch (error) {
            telemetry.warning(error);
            telemetry.debug('Unable to get cart validateCart');
            return AsyncResult.resolve();
        }
        const cartVersion = cartResult.cart?.Version;
        if (cartVersion) {
            try {
                const validationResult: CartLineValidationResults = await validateForCheckoutAsync({ callerContext: this.props.context.actionContext }, cartResult?.cart?.Id, cartVersion);
                this.setState({ cartlinesErrors: validationResult.ValidationFailuresByCartLines || [] });
                return AsyncResult.resolve();
            } catch (error) {
                if (retry && error.name === 'Microsoft_Dynamics_Commerce_Runtime_InvalidCartVersion') {
                    try {
                        await cartResult.refreshCart({});
                    } catch (refreshError) {
                        telemetry.warning(refreshError);
                        telemetry.debug('Unable to refresh Cart validateCart');
                        return AsyncResult.resolve();
                    }
                    try {
                        await this._validateCart(false);
                        return AsyncResult.resolve();
                    } catch (validateError) {
                        telemetry.warning(validateError);
                        telemetry.debug('Unable to validate Cart');
                        return AsyncResult.resolve();
                    }
                }

                telemetry.warning(error);
                telemetry.debug('Unable to validate Cart validateForCheckoutAsync');
                return AsyncResult.resolve();
            }
        } else {
            telemetry.warning('Cart version not found for Cart validation request.');
            return AsyncResult.resolve();
        }
    }

    private async _getOrderQuantityLimitsFeatureFlag(): Promise<boolean> {
        const defaultOrderQuantityLimitsFeatureConfig = this.props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits;
        if (defaultOrderQuantityLimitsFeatureConfig === 'none') {
            return false;
        }

        try {
            const featureStatuses = await this.props.data.featureState;
            const isQuantityLimitsFeatureEnabledInHq = featureStatuses
                .find(featureState => featureState.Name === 'Dynamics.AX.Application.RetailDefaultOrderQuantityLimitsFeature')?.IsEnabled || false;
            if (!isQuantityLimitsFeatureEnabledInHq) {
                return false;
            }
        } catch (error) {
            this.props.telemetry.warning(error);
            this.props.telemetry.debug('Unable to get feature states');
            return false;
        }

        if (defaultOrderQuantityLimitsFeatureConfig === 'all') {
            return true;
        }
        try {
            const customerInfo = await this.props.data.customerInformation;
            return !!customerInfo &&
                ((defaultOrderQuantityLimitsFeatureConfig === 'b2b' && customerInfo.IsB2b) ||
                    (defaultOrderQuantityLimitsFeatureConfig === 'b2c' && !customerInfo.IsB2b));
        } catch (error) {
            this.props.telemetry.warning(error);
            this.props.telemetry.debug('Unable to get customer info order quantity');
            return false;
        }
    }

    // tslint:disable-next-line: max-func-body-length
    private _getCartLinesGroup(): ICartlinesViewProps[][] | null {
        const {
            data: {
                cart: { result: cart },
                wishlists: { result: wishlists },
                orderTemplates: { result: orderTemplates },
                customerInformation: { result: customerInformation },
                productAttributes: { result: productAttributes }
            },
            context: {
                request: {
                    channel: {
                        PickupDeliveryModeCode
                    } = { PickupDeliveryModeCode: undefined }
                }
            },
            config: config,
            telemetry,
            resources,
            modules
        } = this.props;

        const imageSettings = this._getImageSettings(config.imageSettings);
        const isQuantityLimitsFeatureEnabled = this.props.data.featureState.result?.
            find(featureState => featureState.Name === 'Dynamics.AX.Application.RetailDefaultOrderQuantityLimitsFeature')?.IsEnabled || false;
        const cartlinesErrors: LineDataValidationFailure[] = this.state.cartlinesErrors;
        let storeSelectorId: string = '';
        if (modules && Object.keys(modules).length > 0 && modules.storeSelector && modules.storeSelector.length > 0) {
            storeSelectorId = modules.storeSelector[0].id;
        }

        return this.props.data.products && this.props.data.products.result && cart && cart.cart.CartLines && cart.cart.CartLines.length > 0 && this.props.data.channelDeliveryOptionConfig?.result ? (
            CartLineGroupItems({
                cartlines: cart.cart.CartLines,
                storeSelectorModuleId: storeSelectorId,
                cartlinesErrors: cartlinesErrors,
                products: this.props.data.products.result,
                productSpecifications: this.state.productSpecifications,
                productCategoryPath: this.state.productCategoryPath,
                productAvailabilites: this.props.data.productAvailabilites.result,
                productDeliveryOptions: this.props.data.deliveryOptions.result,
                imageSettings: imageSettings,
                isStockCheckEnabled: this.props.context.app.config.enableStockCheck || false,
                maxCartlineQuantity: this.props.context.app.config.maxQuantityForCartLineItem,
                gridSettings: this.props.context.request.gridSettings!,
                context: this.props.context,
                typeName: this.props.typeName,
                id: this.props.id,
                productAttributes,
                pleaseContactExpertForOrder: this.props.config.pleaseContactExpertForOrder,
                sleepExpertContact: this.props.config.sleepExpertContact,
                sleepExpertChat: this.props.config.sleepExpertChat,
                resources: {
                    sizeString: resources.productDimensionTypeSize,
                    colorString: resources.productDimensionTypeColor,
                    configString: resources.productDimensionTypeConfiguration,
                    styleString: resources.productDimensionTypeStyle,
                    amountString: resources.productDimensionTypeAmount,
                    quantityDisplayString: resources.quantityDisplayText,
                    inputQuantityAriaLabel: resources.inputQuantityAriaLabel,
                    productPageLinkAriaLabel: resources.productPageLinkAriaLabel,
                    decrementButtonAriaLabel: resources.decrementButtonAriaLabel,
                    incrementButtonAriaLabel: resources.incrementButtonAriaLabel,
                    discountStringText: resources.discountStringText,
                    originalPriceText: resources.originalPriceText,
                    currentPriceText: resources.currentPriceText,
                    shippingChargesText: resources.shippingCharges,
                    outOfStockMsgText: resources.outOfStockMsgText,
                    removeFromCartText: resources.removeFromCartText,
                    similarAvailableProductText: resources.similarAvailableProductText,
                    removeFromCartTextWithoutBeforeCheckout: resources.removeFromCartTextWithoutBeforeCheckout,
                    homeDeliveryText: resources.homeDeliveryText,
                    // VSI Customization {52133}
                    itemsQuantityText: resources.itemsQuantityText,
                    productAriaText: resources.productAriaText,
                    variantAriaText: resources.variantAriaText,
                    promoAriaText: resources.promoAriaText,
                    priceAriaText: resources.priceAriaText,
                    discountPercentageAriaText: resources.discountPercentageAriaText,
                    editGiftCtaText: resources.editGiftCtaText,
                    dialogAriaText: resources.dialogAriaText,
                    removeButtonAriaText: resources.removeButtonAriaText,
                    appliedPromoText: resources.appliedPromoText,
                    notAppliedPromoText: resources.notAppliedPromoText
                    // VSI Customization {52133} End
                },
                telemetry: telemetry,
                addToWishlistButtonText: resources.addToWishlistButtonText,
                removeFromWishlistButtonText: resources.removeFromWishlistButtonText,
                removeButtonText: resources.removeCartButtonText,
                shipItText: resources.shipInsteadDisplayText,
                pickitUpText: resources.pickItUpDisplayText,
                changeStoreText: resources.changeStoreDisplayText,
                storeSelectorStateManager: this.props.data.storeSelectorStateManager.result,
                outOfStockText: resources.outOfStockErrorMessage,
                outOfRangeOneText: resources.errorMessageOutOfRangeOneLeft,
                outOfRangeFormatText: resources.errorMessageOutOfRangeFormat,
                cartState: this.props.data.cart.result,
                orgUnitLocations: this.props.data.orgUnitLocations.result,
                removeItemClickHandler: this._removeItemFromCartHandler,
                moveToWishlistSuccessHandler: this._moveToWishlistSuccessHandler,
                updateCartLinesQuantitySuccessHandler: this._updateCartlineQuantity,
                locationChangedHandler: this._fulfillmentStoreIdUpdated,
                wishlists: wishlists,
                defaultWishlistName: resources.defaultWishlistName,
                pickupDeliveryModeCode: PickupDeliveryModeCode,
                includeErrors: config.showOutOfStockErrors,
                showShippingChargesForLineItems: config.showShippingChargesForLineItems,
                telemetryContent: this.telemetryContent,
                isQuantityLimitsFeatureEnabled: isQuantityLimitsFeatureEnabled,
                addToOrderTemplateDialogResources: this._generateAddToOrderTemplateDialogResources(resources),
                createOrderTemplateDialogResources: this._generateOrderTemplateNameDialogResources(resources),
                itemAddedToOrderTemplateDialogResources: this._generateItemAddedToOrderTemplateDialogResources(resources),
                duplicateItemsWhenAddingToOrderTemplateDialogResources: this._generateDuplicateItemsWhenAddingToOrderTemplateDialogResources(resources),
                addToOrderTemplateButtonText: resources.addToOrderTemplateButtonText,
                addToOrderTemplateButtonTooltip: resources.addToOrderTemplateButtonTooltip,
                addToOrderTemplateMessage: resources.addToOrderTemplateMessage,
                addItemToOrderTemplateError: resources.addItemToOrderTemplateError,
                orderTemplates: orderTemplates,
                customerInformation: customerInformation,
                channelDeliveryOptionConfig: this.props.data.channelDeliveryOptionConfig?.result,
                sendState: this.state.selectedBonusItems,
                deliveryByMessagesCore: this.state.deliveryByMessagesCore,
                deliveryByMessageSPDS: this.state.deliveryByMessageSPDS,
                hideDeliveryMessage: this.state.disableCheckoutButton
            })
        ) : null;
    }
    /* VSI customization START */
    private _getAllCoupons(props: IMfrmCartProps<IMfrmCartData>): void {
        if (!hasArray(this.props.data.cart.result?.cart?.CartLines)) {
            return;
        }
        // const { context } = props;
        // const getAllBonusCoupons = _getBonusCouponsDetails(context, '');
        // getAllBonusCoupons.then((result) => {
        //     if (result && result !== undefined) {
        //         this.getCoupons.push(result);
        //         localStorage.setItem('bonusCouponsDetail', JSON.stringify(this.getCoupons));
        //         // this.props.context.actionContext.update(new BonusCouponDetailsInput(), { bonusCouponDetail: JSON.stringify(this.getCoupons) });
        //     }
        // }).catch((error) => {
        //     this.props.telemetry.warning(error);
        //     this.props.telemetry.debug('Unable to get Bonus Coupons Details when get all coupons');
        // });
        if (this.props.data.bonusCouponsDetails?.result) {
            this.getCoupons.push(this.props.data.bonusCouponsDetails.result.couponsDetails);
            /* VSI customization START */
            getSetBonusCouponDetail('set', this.getCoupons);
            // localStorage.setItem('bonusCouponsDetail', JSON.stringify(this.getCoupons));
            /* VSI customization END*/
        }
    }

    private _checkPromoCode(props: IMfrmCartProps<IMfrmCartData>): void {
        this.isGiftCoupon = [];
        this.props.data.cart.result?.cart && this.props.data.cart.result?.cart.Coupons?.map((userDefinePromo) => {
            this.getCoupons.map((bonusCoupons) => {
                bonusCoupons.BonusCoupons?.map((coupons) => {

                    if (coupons.BonusCoupon === userDefinePromo.Code) {
                        this.isGiftCoupon.push(coupons);
                    } else {
                        coupons.ChildCoupons?.map((childCoupons) => {
                            if (childCoupons === userDefinePromo.Code) {
                                if (this.isGiftCoupon.indexOf(coupons) === -1) {
                                    this.isGiftCoupon.push(coupons);
                                }
                            }
                        });
                    }

                    return [];
                });
            });
        });
        if (this.isGiftCoupon && this.isGiftCoupon.length > 0) {
            this._getBonusItems(this.isGiftCoupon, this.props);
            this.callFirstTime = true;
        }
    }
    // tslint:disable-next-line: typedef
    private _reconstructData = async (globalBonusItems: IMFIBonusItemsResponse[]) => {
        const testVarStruct = globalBonusItems;
        for (const obj of testVarStruct) {
            const deets = obj.BonusItemsDetails;
            for (const dt of deets!) {
                const coupon = dt.Coupon;
                const itms = dt.Items;
                for (const it of itms!) {
                    const items = it.BonusItems;
                    const qItemId = it.QualifyingItemId;
                    const qpId = it.QualifyingProductId;
                    for (const itm of items!) {
                        itm.QualifyingItemId = qItemId;
                        itm.Coupon = coupon;
                        itm.QualifyingProductId = qpId;
                    }
                }
            }
        }
        return testVarStruct;
    };

    // tslint:disable-next-line: typedef
    private _getBonusItemsBrandName = async (globalBonusItems: IMFIBonusItemsResponse[]) => {
        const {
            context: {
                actionContext: ctx,
                request: {
                    apiSettings: {
                        channelId
                    }
                }
            }
        } = this.props;
        const testVarStruct = globalBonusItems;
        const bonusItemsBrandName: IProductBonusWithBrandName[] = [];
        testVarStruct.map((obj) => {
            const deets = obj.BonusItemsDetails;
            deets?.map((items) => {
                items?.Items?.map((bonasItems) => {
                    bonasItems.BonusItems?.map(async (bonusArray) => {
                        const productId = Number(bonusArray.BonusProductId);
                        const productSpecificationData: IAttributeValueProduct[] = await getAttributeValuesAsync({ callerContext: ctx, queryResultSettings: {} }, productId, channelId, 0);
                        const newProductSpecificationData = productSpecificationData.filter(Boolean);
                        let brandNameGet = '';
                        if (newProductSpecificationData) {
                            brandNameGet = newProductSpecificationData.find(item => item.Name === 'Brand')?.TextValue!;
                        }
                        const productWithBrand = { productId, brandName: brandNameGet };
                        const getExist = bonusItemsBrandName.includes(productWithBrand);
                        if (!getExist) {
                            bonusItemsBrandName.push(productWithBrand);
                        }
                        /* VSI customization START */
                        getSetBonusItemBrandName('set', bonusItemsBrandName);
                        // localStorage.setItem('bonusItmesWithBrand', JSON.stringify(bonusItemsBrandName));
                        /* VSI customization END*/
                    });
                });
            });
        });
    };

    // tslint:disable-next-line:max-func-body-length
    private _getBonusItems(bonusItems: IMFIBonusCouponsDetail[], props: IMfrmCartProps<IMfrmCartData>): void {
        const {
            context,
            data: {
                cart: { result: cart }
            },
            telemetry
        } = props;
        this.globalBonusItems = [];
        let productQuantityCount: number | undefined;
        const bonusItemsParams: string[] = [];
        const bonusCartlines: CartLine[] | null = [];
        bonusItems.map((bonusValue) => {
            if (bonusValue.BonusCoupon) {
                bonusItemsParams.push(bonusValue.BonusCoupon);
            }
        });
        if (props.data.cart.result?.cart.Id) {
            localStorage.removeItem('BonusItemProduct');
            const requestBodyMFIBonus: IMFIGetBonusItemsParameters = {
                CartId: props.data.cart.result?.cart.Id,
                Coupons: bonusItemsParams
            };
            // After applying promotion, used stored mfiBonusItems API response to prevent duplicate calls
            const mfiBonusItems = this.props.data.mfiBonusItems.result;
            const getBonusItemsProducts = _getMFIGetBonusItems(context, requestBodyMFIBonus, mfiBonusItems);

            getBonusItemsProducts.then(async (bonusItemsResponse) => {
                this.props.context.actionContext.update(new MFIbonusitemsInput(), bonusItemsResponse?.BonusItemsDetails);
                //  this.props.context.actionContext.update(new MFIbonusitemsInput(), { bonusItems: bonusItemsResponse?.BonusItemsDetails, message: bonusItemsResponse?.Message });

                if (bonusItemsResponse !== undefined) {
                    // bonusItemsResponse !== null && localStorage.setItem('BonusItemProduct', JSON.stringify(bonusItemsResponse));
                    const bonusItemResult = bonusItemsResponse;
                    const checkExist = this.globalBonusItems.includes(bonusItemResult!);
                    if (!checkExist) {
                        bonusItemResult && this.globalBonusItems.push(bonusItemResult);
                    }
                    const cartState = await getCartState(props.context.actionContext);
                    this._getBonusItemsBrandName(this.globalBonusItems);
                    const bonusLocalStorageData = await this._reconstructData(this.globalBonusItems);
                    /* VSI customization START */
                    getSetBonusItemProd('set', bonusLocalStorageData);
                    this.setState({ isBonusItemProductUpdated: true });
                    // localStorage.setItem('BonusItemProduct', JSON.stringify(bonusLocalStorageData));
                    /* VSI customization END*/
                    bonusItemResult?.BonusItemsDetails?.map((bonusItemProducts) => {
                        bonusItemProducts.Items?.map((item) => {
                            cart?.cart.CartLines?.filter((cartline: CartLine) => {
                                if (item.QualifyingItemId === cartline.ItemId) {
                                    productQuantityCount = cartline.Quantity;
                                }
                            });
                            item.BonusItems?.map(async (product) => {

                                let checkItemAlredyInCart: boolean = false;
                                cart?.cart.CartLines?.filter((cartline: CartLine) => {
                                    if (product.BonusItemId === cartline.ItemId) {
                                        checkItemAlredyInCart = true;
                                    }
                                });

                                if (!checkItemAlredyInCart && product.AdditionToCart === 'Auto') {
                                    const productToAdd = {
                                        ProductId: Number(product.BonusProductId),
                                        // ProductTypeValue: 5,
                                        EntryMethodTypeValue: 5,
                                        ItemId: product.BonusItemId,
                                        Quantity: productQuantityCount
                                    };
                                    bonusCartlines.push(productToAdd);
                                }
                                this._addGiftCta();
                            });
                        });
                    });
                    const {
                        context: {
                            request: {
                                apiSettings: {
                                    channelId,
                                    catalogId
                                }
                            }
                        }
                    } = this.props;
                    const cookies = new Cookies();
                    const zipcode = cookies.get('zip_code');
                    if (bonusCartlines && bonusCartlines.length > 0) {
                        this._validateCart().then(async () => {
                            this.props.context.actionContext.update(new PromoCodeAppliedOnCartInput(), { isInProcess: true });
                            await addCartLinesAsync({ callerContext: this.props.context.actionContext }, cart!.cart.Id, bonusCartlines).then(async newCart => {
                                await cartState.refreshCart({});
                                this.props.context.actionContext.update(new PromoCodeAppliedOnCartInput(), { isInProcess: false });
                                reshuffleItems(this.props.data.cart.result?.cart.CartLines ? this.props.data.cart.result?.cart.CartLines : []);
                                this.refreshCartCookie();
                                this._getStateFromZipCode(this.props, zipcode, true);
                                getAttributesForCartLineItems(new AttributesForSelectedVariantInput(channelId, catalogId), this.props.context.actionContext).then((res) => {
                                    this.props.context.actionContext.update(new AttributesForSelectedVariantInput(channelId, catalogId), res);
                                    this.returnCoreProductCheck();
                                    this.setState({ productAttributes: res });
                                }).catch((error) => {
                                    telemetry.warning(error);
                                    telemetry.debug('Unable to validate Attributes CartLine Items');
                                });
                            });
                        }).catch((error) => {
                            telemetry.warning(error);
                            telemetry.debug('Unable to validate Cart when get bonus items');
                        });
                    }
                }
            }).catch((error) => {
                telemetry.warning(error);
                telemetry.debug('Unable to mFIGetBonusItemsDetailAsync when get bonus items');
            });
        }
    }

    private _addGiftCta(): void {
        /* VSI customization START */
        const getLocalStorage = getSetBonusItemProd('get');
        // const getLocalStorage = localStorage.getItem('BonusItemProduct');
        /* VSI customization END*/
        let bonusItemArray: IMFIBonusItemsResponse[] = [];
        let bonusProductCount: number = 0;
        let bonusItemLength: number | undefined;
        let bonusProductActualStatus: number = 0;
        let manualBoniusItems: number = 0;
        const bonusItemsArrayProduct = [];
        if (getLocalStorage) {
            bonusItemArray = getLocalStorage;
            if (hasArray(bonusItemArray)) {
                bonusItemArray?.map((bonusitemdetail) => {
                    bonusitemdetail.BonusItemsDetails?.map((bonusitem) => {
                        bonusitem.Items?.map((item) => {
                            bonusItemLength = item.BonusItems?.length;
                            bonusProductActualStatus = item.BonusProductQty;

                            item.BonusItems?.map((finalItem) => {
                                this.props.data.cart.result?.cart.CartLines?.filter((cartline) => {
                                    if (finalItem.AdditionToCart === 'Manual') {
                                        bonusItemsArrayProduct.push(finalItem);
                                    }
                                    if (finalItem.BonusItemId === cartline.ItemId) {
                                        bonusProductCount++;
                                    }
                                    if (finalItem.BonusItemId === cartline.ItemId && finalItem.AdditionToCart === 'Manual') {
                                        manualBoniusItems++;
                                    }
                                });
                            });

                        });
                    });
                });
            }
            if (bonusItemsArrayProduct.length > 0) {
                if (bonusProductActualStatus === manualBoniusItems) {
                    this.ctaGiftButton = false;
                } else {
                    this.ctaGiftButton = false;
                }
            } else {
                if (bonusItemLength === bonusProductCount) {
                    this.ctaGiftButton = false;
                } else {
                    this.ctaGiftButton = false;
                }
            }
        }
    }

    // tslint:disable-next-line
    private async _showCtaGiftProducts(cartlineTo: CartLine, context: any, cartlines: CartLine[] | undefined): Promise<void> {
        const {
            data: {
                cart: { result: cart }
            },
            telemetry,
            context: {
                actionContext: actionContext
            }
        } = this.props;
        /* VSI customization START */
        const getLocalStorage = getSetBonusItemProd('get');
        // const getLocalStorage = localStorage.getItem('BonusItemProduct');
        /* VSI customization END*/
        const cartState = await getCartState(actionContext);
        const bonusCartlines: CartLine[] | null = [];
        let bonusItemArray: IMFIBonusItemsResponse[] = [];
        let productQuantityCount: number | undefined;
        if (getLocalStorage) {
            bonusItemArray = getLocalStorage;
            bonusItemArray?.map((bonusitemdetail) => {
                bonusitemdetail.BonusItemsDetails?.map((bonusitem) => {
                    bonusitem.Items?.map((item) => {
                        if (item.QualifyingItemId === cartlineTo.ItemId) {
                            productQuantityCount = cartlineTo.Quantity;
                            item.BonusItems?.map(async (product) => {
                                let checkItemAlredyInCart: boolean = false;
                                cartlines?.filter((cartline: CartLine) => {
                                    if (product.BonusItemId === cartline.ItemId) {
                                        checkItemAlredyInCart = true;
                                    }
                                });

                                if (!checkItemAlredyInCart && product.AdditionToCart === 'Auto') {
                                    const productToAdd = {
                                        ProductId: Number(product.BonusProductId),
                                        // ProductTypeValue: 5,
                                        EntryMethodTypeValue: 5,
                                        ItemId: product.BonusItemId,
                                        Quantity: productQuantityCount
                                    };
                                    bonusCartlines.push(productToAdd);
                                }
                            });
                        }
                    });
                });
            });
            if (bonusCartlines.length > 0) {
                const {
                    context: {
                        request: {
                            apiSettings: {
                                channelId,
                                catalogId
                            }
                        }
                    }
                } = this.props;
                const cookies = new Cookies();
                const zipcode = cookies.get('zip_code');
                this._validateCart().then(async () => {
                    this.props.context.actionContext.update(new PromoCodeAppliedOnCartInput(), { isInProcess: true });
                    await addCartLinesAsync({ callerContext: actionContext }, cart!.cart.Id, bonusCartlines).then(async newCart => {
                        await cartState.refreshCart({});
                        this.props.context.actionContext.update(new PromoCodeAppliedOnCartInput(), { isInProcess: false });
                        reshuffleItems(this.props.data.cart.result?.cart.CartLines ? this.props.data.cart.result?.cart.CartLines : []);
                        this.refreshCartCookie();
                        this._getStateFromZipCode(this.props, zipcode, true);
                        getAttributesForCartLineItems(new AttributesForSelectedVariantInput(channelId, catalogId), actionContext).then((res) => {
                            actionContext.update(new AttributesForSelectedVariantInput(channelId, catalogId), res);
                            this.returnCoreProductCheck();
                            this.setState({ productAttributes: res });
                        }).catch((error) => {
                            telemetry.warning(error);
                            telemetry.debug('Unable to validate Attributes CartLine Items show gift products');
                        });
                    });
                }).catch((error) => {
                    telemetry.warning(error);
                    telemetry.debug('Unable to validate Cart show gift products');
                });
            }
        }
    }

    /* VSI customization END */
}

export default Cart;
